import { Link } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";

import { ReactComponent as AMark } from "MVVM/Assets/Brand/A-Mark-Light.svg";

import { rgba } from "../../../utils";
import { DropdownMenu } from "MVVM/Components/DropdownMenu";

export const Page = styled.div`
  background: ${({ theme }) => theme.colors.navy600};
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.ice};
  min-height: 100%;
  width: 100%;

  ::selection {
    background: ${({ theme }) => theme.colors.envyAlpha40};
    color: ${({ theme }) => theme.colors.ice};
  }
`;

// Top space is padding, not the header's margin, which collapsed out of the
// dark page and showed the app's lighter background as a band under the nav.
export const PageBody = styled.div`
  box-sizing: border-box;
  margin: 0 auto;
  max-width: 1280px;
  padding: 20px 24px 60px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    padding: 12px 16px 40px;
  }
`;

// Win Probability under the Allstar Rating breakdown, on the same tab.
export const WinSection = styled.section`
  margin-top: 40px;

  > h2 {
    font-size: 20px;
    font-weight: 700;
    margin: 0 0 14px;
  }
`;

export const Section = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  margin-bottom: 16px;
  padding: 16px;
`;

export const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.04em;
  margin: 0 0 12px;
  text-transform: uppercase;
`;

// The card is painted on the body cells, not a wrapper, so the header row can
// sit outside it while the columns stay in one table and line up for free.
export const Table = styled.table<{ $compact?: boolean }>`
  border-collapse: separate;
  border-spacing: 0;
  font-size: 16px;
  // Stat columns only line up if the digits are the same width.
  font-variant-numeric: tabular-nums;
  width: 100%;

  th,
  td {
    font-weight: 500;
    // Metrics were colliding at 10px; they need room to read as separate
    // columns rather than one block of digits.
    padding: 11px 14px;
    text-align: right;
    white-space: nowrap;
  }

  // The player column stays put while the stats scroll under it, so a row is
  // never a set of numbers belonging to nobody.
  th:first-child,
  td:first-child {
    left: 0;
    position: sticky;
    z-index: 1;
  }

  // Body cells carry the card colour; the header sits outside the card, on the
  // page background.
  th:first-child {
    background: ${({ theme }) => theme.colors.ned};
  }

  // Below the medium break only six columns survive; the rest come back with
  // the More stats toggle rather than living behind an undiscoverable scroll.
  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    ${({ $compact }) =>
      $compact &&
      css`
        th:nth-child(n + 7),
        td:nth-child(n + 7) {
          display: none;
        }
      `}
  }

  th {
    padding-bottom: 8px;
  }

  // Scoped so the team label in the first cell keeps its own styling without
  // needing !important to win.
  th:not(:first-child) {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  td {
    background: ${({ theme }) => theme.colors.navy200};
  }

  tbody tr:not(:first-child) td {
    border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  }

  tbody tr:first-child td:first-child {
    border-top-left-radius: 10px;
  }

  tbody tr:first-child td:last-child {
    border-top-right-radius: 10px;
  }

  tbody tr:last-child td:first-child {
    border-bottom-left-radius: 10px;
  }

  tbody tr:last-child td:last-child {
    border-bottom-right-radius: 10px;
  }

  // Team label sits flush with the page edge and the rows indent under it, so
  // the scoreboard reads as a list belonging to that heading.
  th:first-child {
    padding-left: 0;
    text-align: left;
    width: 100%;
  }

  td:first-child {
    padding-left: 14px;
    text-align: left;
    width: 100%;
  }

  th:last-child,
  td:last-child {
    padding-right: 22px;
  }

  // Breathing room between the K/D/A group and the rest of the metrics.
  th:nth-child(2),
  td:nth-child(2) {
    padding-right: 26px;
  }
`;
export const List = styled.ul`
  font-size: 13px;
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
    display: flex;
    gap: 12px;
    justify-content: space-between;
    padding: 6px 0;
  }
`;

export const Muted = styled.p`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
  margin: 0;
`;

export const RankBadgeWrap = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 8px;
`;

export const RankIcon = styled.img`
  height: 20px;
  width: auto;
`;

export const RankSwatch = styled.span<{ $color: string }>`
  background: ${({ $color }) => $color};
  border-radius: 2px;
  display: inline-block;
  height: 10px;
  width: 10px;
`;

// Drawn in CSS rather than using the plate SVG: that art has grey gradients
// baked in, so it cannot take the rating's tier colour.
export const PremierPlate = styled.span<{ $color: string }>`
  align-items: center;
  display: inline-flex;

  i {
    background: ${({ $color }) => $color};
    height: 20px;
    transform: skewX(-14deg);
    width: 3px;
  }

  // Both bars and the band carry the same 14deg skew, so these gaps are just
  // the margins. The pair reads as one mark, sitting right up against the band.
  i:first-child {
    margin-right: 2px;
  }

  i:nth-child(2) {
    margin-right: 2px;
  }

  // Dark plate carrying the rating in the tier colour, matching the bars beside
  // it, rather than light ink on a bright band.
  span {
    background: ${({ $color, theme }) =>
      `linear-gradient(105deg, ${rgba($color, 0.28)} 0%, ${rgba(
        $color,
        0.14,
      )} 100%), ${theme.colors.midnight}`};
    border: 1px solid ${({ $color }) => rgba($color, 0.55)};
    border-radius: 2px;
    padding: 2px 7px;
    transform: skewX(-14deg);
  }

  em {
    color: ${({ $color }) => $color};
    display: block;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    letter-spacing: 0.01em;
    transform: skewX(14deg);
  }
`;
export const RankArrow = styled.span<{ $promoted: boolean }>`
  align-items: center;
  color: ${({ $promoted, theme }) =>
    $promoted ? theme.colors.statElite : theme.colors.statWeak};
  display: inline-flex;
  flex-shrink: 0;
  font-size: 14px;
`;

// A true minus sign rather than a hyphen, and tabular figures, so a column of
// these lines up instead of jittering by a pixel per row.
export const RankDelta = styled.span<{ $promoted: boolean }>`
  color: ${({ theme }) => theme.colors.ice};
  flex-shrink: 0;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
`;

export const PlayerCell = styled.span`
  align-items: center;
  display: flex;
  font-size: 17px;
  font-weight: 700;
  gap: 10px;
`;

// Name leads, rating sits under it. Inline, the plate pushed every name to a
// different x position and the column stopped scanning as a column.
export const PlayerText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

// A real anchor, so the row can be opened in a new tab the way a name that
// looks like a link ought to be.
export const PlayerLink = styled(Link)`
  align-items: center;
  color: inherit;
  display: inline-flex;
  gap: 6px;
  min-width: 0;
  text-decoration: none;

  &:hover span[data-name],
  &:focus-visible span[data-name] {
    color: ${({ theme }) => theme.colors.envy};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 3px;
  }

  // Capped: this column was sizing itself to the longest name in the lobby,
  // which set the whole table's minimum width and pushed the two column
  // breakpoint up past common laptops. The full name stays in the title.
  span[data-name] {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: color 0.15s ease;
    white-space: nowrap;
  }
`;

// Marks the players who actually have an Allstar account behind the name.
export const AllstarMark = styled(AMark)`
  flex-shrink: 0;
  height: 13px;
  width: 13px;
`;

export const Avatar = styled.img`
  border-radius: 50%;
  flex-shrink: 0;
  height: 32px;
  object-fit: cover;
  width: 32px;
`;

export const AvatarFallback = styled.span`
  align-items: center;
  background: ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 50%;
  display: inline-flex;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
  height: 32px;
  justify-content: center;
  width: 32px;
`;

// The row above the header: where you came from, and the one share action.
export const HeroBar = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin: 0 0 14px;
`;

export const Crumbs = styled.nav`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
  min-width: 0;

  a {
    color: ${({ theme }) => theme.colors.chalk};
    flex: none;
    text-decoration: none;
  }

  a:hover {
    color: ${({ theme }) => theme.colors.ice};
  }

  a:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  [aria-current] {
    color: ${({ theme }) => theme.colors.shade200};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// Items here run longer than the shared menu's fixed width.
export const MatchMenuWrap = styled(DropdownMenu)`
  flex: none;

  // The open menu only, never the trigger beside it.
  > div:nth-child(2) button {
    width: auto;
    min-width: 176px;
    white-space: nowrap;
  }
`;

export const MatchMenuButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.iceAlpha08};
  border: 0;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha20};
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: inline-flex;
  height: 36px;
  justify-content: center;
  transition: background 0.15s ease;
  width: 36px;

  &:hover {
    background: ${({ theme }) => rgba(theme.colors.ice, 0.14)};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

const STRIP = "#16222a";
const STRIP_H = 60;

// The score sits in the open band; the match details run along its footer.
// The map art is its own blurred layer, clipped to the band, so agents can
// still stand above the band's top edge.
export const Hero = styled.header<{ $art?: string }>`
  background: ${({ theme }) => theme.colors.navy900};
  isolation: isolate;

  &::before {
    background:
      linear-gradient(
        180deg,
        ${rgba("#111b22", 0.6)},
        ${rgba("#111b22", 0.85)}
      ),
      ${({ $art }) => ($art ? `url(${$art})` : "none")} center 40% / cover
        no-repeat;
    clip-path: inset(0 round 16px);
    content: "";
    filter: blur(4px);
    inset: 0;
    position: absolute;
    z-index: -1;
  }

  border-radius: 16px;
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha20};
  box-sizing: border-box;
  margin: 0 0 6px;
  padding-top: 24px;
  position: relative;

  &[data-mvps] {
    margin-top: 64px;
    padding-top: 44px;
  }

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    &[data-mvps] {
      margin-top: 0;
      padding-top: 24px;
    }
  }
`;

export const ScoreLine = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 28px;
  justify-content: center;
  padding: 0 16px 22px;
  position: relative;

  > i {
    color: ${({ theme }) => rgba(theme.colors.ice, 0.3)};
    font-size: 60px;
    font-style: normal;
    font-weight: 500;
    line-height: 0.95;
  }

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    gap: 16px;

    > i {
      font-size: 40px;
    }
  }
`;

// Only your score takes the result color; both team names stay neutral.
export const ScoreSide = styled.div<{ $mine?: boolean; $won?: boolean }>`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  b {
    color: ${({ $mine, $won, theme }) =>
      !$mine
        ? theme.colors.iceAlpha60
        : $won === undefined
        ? theme.colors.ice
        : $won
        ? theme.colors.envy
        : theme.colors.statPoor};
    font-size: 76px;
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 0.95;
  }

  small {
    color: ${({ $mine, theme }) =>
      $mine ? theme.colors.shade200 : theme.colors.chalk};
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 1px;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    text-transform: uppercase;
    white-space: nowrap;
  }

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    b {
      font-size: 48px;
    }

    small {
      font-size: 11px;
      max-width: 120px;
    }
  }
`;

export const MvpRating = styled.span<{ $tone: TStatTone }>`
  align-items: center;
  color: ${({ $tone, theme }) => theme.colors[TONE_COLOR[$tone]]};
  display: flex;
  flex: none;
  font-size: 18px;
  font-style: italic;
  letter-spacing: 0.03em;
  font-variant-numeric: tabular-nums;
  font-weight: 900;
  gap: 6px;

  svg {
    height: 22px;
    overflow: visible;
    width: 22px;
  }

  [data-outline] path {
    fill: none;
    stroke-linejoin: round;
  }

  [data-outline] path:first-child {
    stroke: ${({ theme }) => rgba(theme.colors.chalk, 0.6)};
    stroke-width: 1.8;
  }

  [data-outline] path:last-child {
    stroke: currentColor;
    stroke-linecap: round;
    stroke-width: 2.2;
  }
`;

export const ModeChip = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-weight: 700;

  em {
    font-style: italic;
  }
`;

// A solid strip along the band's bottom: one centered line, one type size.
export const HeroMeta = styled.div`
  align-items: center;
  background: ${STRIP};
  border-radius: 0 0 16px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.shade200};
  display: flex;
  flex-wrap: wrap;
  font-size: 14px;
  font-weight: 500;
  // Space, not dots, between the details.
  gap: 4px 28px;
  justify-content: center;
  min-height: ${STRIP_H}px;
  padding: 8px 280px;
  position: relative;
  z-index: 1;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    min-height: 48px;
    padding: 8px 20px;
  }

  > span {
    align-items: center;
    display: inline-flex;
    gap: 8px;
    white-space: nowrap;
  }

  img {
    height: 20px;
    object-fit: contain;
    width: 20px;
  }

  b {
    color: ${({ theme }) => theme.colors.ice};
    font-weight: 700;
  }
`;

// Each team's MVP stands at its end of the band, lit in the side they finished on.
export const TeamMvp = styled.figure<{
  $us?: boolean;
  $side: "T" | "CT";
  $match?: boolean;
}>`
  align-items: flex-end;
  bottom: 0;
  display: flex;
  height: 250px;
  margin: 0;
  pointer-events: none;
  position: absolute;
  width: 300px;
  z-index: 2;
  ${({ $us }) => ($us ? "left: 0;" : "right: 0;")}

  &::before {
    background: radial-gradient(
      70% 80% at ${({ $us }) => ($us ? "30%" : "70%")} 100%,
      ${({ $side }) => rgba($side === "CT" ? "#6ea8dc" : "#d9a54a", 0.27)},
      transparent 70%
    );
    border-radius: ${({ $us }) => ($us ? "16px 0 0 16px" : "0 16px 16px 0")};
    content: "";
    inset: 0;
    position: absolute;
  }

  > img {
    bottom: ${STRIP_H}px;
    filter: drop-shadow(0 12px 24px rgba(0, 0, 0, 0.55));
    height: 210px;
    mask: linear-gradient(180deg, #000 55%, transparent 98%);
    position: absolute;
    width: auto;
    ${({ $us }) =>
      $us ? "left: -36px; transform: scaleX(-1);" : "right: -36px;"}
  }

  // The plate is the footer strip's end cap: same height, same solid color.
  figcaption {
    align-items: center;
    background: ${STRIP};
    border-radius: ${({ $us }) => ($us ? "0 0 0 16px" : "0 0 16px 0")};
    bottom: 0;
    // The strip's own top line carries on across the plate, no divider.
    border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
    box-sizing: border-box;
    display: flex;
    gap: 12px;
    height: ${STRIP_H}px;
    justify-content: space-between;
    padding: 0 16px 0 20px;
    position: absolute;
    width: 260px;
    ${({ $us }) => ($us ? "left: 0;" : "right: 0;")}
  }

  figcaption [data-who] {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
  }

  figcaption [data-avatar] {
    border-radius: 50%;
    flex: none;
    height: 28px;
    object-fit: cover;
    width: 28px;
  }

  // Match MVP gets an Allstar green label, Team MVP a silver one.
  figcaption small {
    align-self: flex-start;
    background: ${({ $match }) => ($match ? "#4dbd94" : rgba("#c7d4d7", 0.18))};
    border-radius: 4px;
    color: ${({ $match, theme }) =>
      $match ? "#062118" : theme.colors.shade200};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    margin-bottom: 2px;
    padding: 1px 6px;
    text-transform: uppercase;
  }

  figcaption b {
    font-size: 18px;
    font-style: italic;
    font-weight: 900;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // The score line needs the whole band on a phone.
  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    display: none;
  }
`;

export const Highlights = styled.section`
  margin-bottom: 24px;

  @media (min-width: 1300px) {
    display: flex;
    flex-direction: column;
    flex: 1;
    margin-bottom: 0;
    min-height: 0;
  }
`;

export const HighlightsTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 0;

  // How many there are in total, next to the name rather than in the status
  // line, which counts states rather than the set.
  b {
    color: ${({ theme }) => theme.colors.chalk};
    font-weight: 700;
    margin-left: 4px;
  }
`;

// Map art stands in until the real thumbnail exists, dimmed hard so it never
// reads as the finished clip.
// Ice glyph on envy, matching the Clip pill. A dark triangle on the same green
// made the two calls to action look like two different systems.
export const PlayBadge = styled.span<{ $small?: boolean }>`
  background: ${({ theme }) => theme.colors.envy};
  border: 0;
  border-radius: 50%;
  box-shadow: 0 2px 10px ${({ theme }) => theme.colors.midnightAlpha60};
  height: ${({ $small }) => ($small ? "22px" : "44px")};
  position: relative;
  transition: filter 0.15s ease;
  width: ${({ $small }) => ($small ? "22px" : "44px")};

  &::after {
    border-bottom: ${({ $small }) => ($small ? "4px" : "7px")} solid transparent;
    border-left: ${({ $small }) => ($small ? "7px" : "12px")} solid
      ${({ theme }) => theme.colors.ice};
    border-top: ${({ $small }) => ($small ? "4px" : "7px")} solid transparent;
    content: "";
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-40%, -50%);
  }
`;

export const EmptyState = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  padding: 28px 20px;
  text-align: center;
`;

export const MomentRowWrap = styled.div`
  align-items: center;
  display: flex;
  gap: 3px;
  margin-top: 6px;
`;

export const MomentIcon = styled.img`
  height: 16px;
  margin-right: 3px;
  width: auto;
`;

export const VictimAvatar = styled.img`
  border-radius: 50%;
  height: 18px;
  object-fit: cover;
  width: 18px;
`;

// Two tabs stretched across 1440px read as a segmented control, not navigation.
// One rule running the full width of the banner, with only the active tab's
// segment lit. The shared Tabs component keeps its analytics and ?tab= syncing;
// only its presentation is overridden here.
export const TabsWrap = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  margin-bottom: 24px;

  // Five tabs do not fit a phone. Scroll the strip rather than letting it push
  // the hero wider, and hide the bar because the rule underneath is the edge.
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > div {
    gap: 36px;
    width: max-content;
    min-width: 100%;
  }

  // Rebass stretches each tab with flex: 1 1 0; these should hug their labels.
  > div > * {
    border-bottom-color: transparent;
    color: ${({ theme }) => theme.colors.chalk};
    cursor: pointer;
    flex: 0 0 auto;
    border-bottom-width: 3px;
    margin-bottom: -1px;
    padding: 14px 0 12px;
  }

  > div > * > div {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  > div > [aria-selected="true"] {
    border-bottom-color: ${({ theme }) => theme.colors.envy};
    color: ${({ theme }) => theme.colors.ice};
  }

  > div > *:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

// The cards carry their own call to action on the thumbnail now, so this is
// just the button an empty state needs to offer a way forward.
export const GhostButton = styled.button`
  align-self: center;
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha40};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  margin-top: 12px;
  padding: 8px 14px;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.envyAlpha10};
    border-color: ${({ theme }) => theme.colors.envy};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

// Graded lime / white / amber / coral. Brand green is kept for "yours".
export const TONE_COLOR = {
  elite: "statElite",
  strong: "statStrong",
  even: "ice",
  weak: "statWeak",
  poor: "statPoor",
} as const;

export type TStatTone = keyof typeof TONE_COLOR;

export const StatValue = styled.span<{
  $tone: TStatTone;
}>`
  color: ${({ $tone, theme }) => theme.colors[TONE_COLOR[$tone]]};
  font-variant-numeric: tabular-nums;
  font-weight: 700;

  small {
    color: ${({ theme }) => theme.colors.chalkAlpha40};
    font-size: inherit;
    font-weight: 500;
    margin: 0 3px;
  }
`;

// The VOD is the biggest artifact this match produced, so its panel borrows the
// banner's own atmosphere rather than sitting in a dashed placeholder box. One
// stage serves every state, so the tab never reads as scaffolding.
export const VodStage = styled.section<{ $art?: string }>`
  align-items: flex-start;
  background-color: ${({ theme }) => theme.colors.midnight};
  border-radius: 10px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 340px;
  overflow: hidden;
  padding: 28px;
  position: relative;

  // Same technique the banner uses: blur on a pseudo element, overscanned so it
  // does not fade at the edges.
  &::before {
    background: ${({ $art }) => ($art ? `url(${$art})` : "none")} center / cover
      no-repeat;
    content: "";
    filter: blur(3px);
    inset: -20px;
    position: absolute;
  }

  &::after {
    background: radial-gradient(
        ellipse 70% 90% at 20% 100%,
        ${({ theme }) => theme.colors.midnightAlpha80} 0%,
        transparent 100%
      ),
      linear-gradient(
        180deg,
        ${({ theme }) => theme.colors.midnightAlpha50} 0%,
        ${({ theme }) => theme.colors.midnightAlpha80} 100%
      );
    content: "";
    inset: 0;
    position: absolute;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    min-height: 300px;
    padding: 20px;
  }
`;

// The house display voice, the one the nav headlines use. The panel was opting
// out of it with an 18px title.
export const VodHeadline = styled.h2`
  color: ${({ theme }) => theme.colors.ice};
  font-size: clamp(28px, 4vw, 44px);
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.02;
  margin: 0 0 10px;
  max-width: 15ch;
  text-shadow: 0 2px 12px ${({ theme }) => theme.colors.midnightAlpha80};
  text-transform: uppercase;
`;

export const VodLede = styled.p`
  // Solid, not iceAlpha75: translucent body text over artwork cannot hold a
  // contrast floor no matter how heavy the scrim behind it gets. Hierarchy
  // comes from size against a 44px display line instead.
  color: ${({ theme }) => theme.colors.ice};
  font-size: 15px;
  line-height: 1.5;
  margin: 0 0 18px;
  max-width: 46ch;
  text-shadow: 0 1px 6px ${({ theme }) => theme.colors.midnightAlpha80};
`;

// One tick per round, straight off the VOD's own markers, lit where the round
// produced a moment. It says "a full match, scrubbable, and these bits are
// yours" without a word of copy.
export const RoundRibbon = styled.div`
  align-items: flex-end;
  display: flex;
  gap: 3px;
  height: 32px;
  margin-bottom: 16px;
  max-width: 520px;
  width: 100%;
`;

export const RoundTick = styled.span<{ $hot?: boolean; $muted?: boolean }>`
  background: ${({ $hot, $muted, theme }) =>
    $muted
      ? theme.colors.chalkAlpha20
      : $hot
      ? theme.colors.envy
      : theme.colors.chalkAlpha40};
  border-radius: 2px;
  flex: 1 1 0;
  height: ${({ $hot }) => ($hot ? "100%" : "52%")};
  min-width: 2px;
  transform-origin: bottom;

  @media (prefers-reduced-motion: no-preference) {
    animation: vodTickIn 420ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  @keyframes vodTickIn {
    from {
      transform: scaleY(0.35);
    }
    to {
      transform: scaleY(1);
    }
  }
`;

export const VodFooter = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px 18px;
`;

// The same envy pill the highlight cards use for their call to action.
export const VodCta = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.envy};
  border: 0;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  gap: 8px;
  padding: 11px 22px;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(1.1);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.ice};
    outline-offset: 3px;
  }
`;

export const VodNote = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  display: inline-flex;
  font-size: 13px;
  font-weight: 500;
  gap: 6px;
  text-shadow: 0 1px 6px ${({ theme }) => theme.colors.midnightAlpha80};
`;

// --- Pre-content states -----------------------------------------------------
// A dead end keeps the match's shape: the header band with nothing in it, then
// the message under it, so the viewer still knows where they are.

export const EmptyHero = styled(Hero)`
  margin-bottom: 28px;

  // No map to show, so a desaturated stand-in for map art.
  &::before {
    background: linear-gradient(
        180deg,
        ${rgba("#131d25", 0.25)},
        ${rgba("#131d25", 0.85)}
      ),
      linear-gradient(
        120deg,
        #6b5a3e 0%,
        #3b4a52 45%,
        #2a3842 70%,
        #7a6243 100%
      );
    filter: blur(1px) saturate(0.4);
  }
`;

export const EmptyScore = styled(ScoreLine)<{ $tone: "alert" | "quiet" }>`
  align-items: center;
  color: ${({ $tone, theme }) =>
    $tone === "alert"
      ? rgba(theme.colors.statPoor, 0.35)
      : rgba(theme.colors.ice, 0.22)};
  padding: 34px 16px 56px;

  b {
    font-size: 84px;
    font-style: italic;
    font-weight: 900;
    line-height: 0.86;
  }

  > i {
    color: inherit;
  }

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    padding: 20px 16px 36px;

    b {
      font-size: 60px;
    }
  }
`;

export const EmptyMeta = styled(HeroMeta)`
  > span {
    background: ${({ theme }) => rgba(theme.colors.ice, 0.08)};
    border-radius: 999px;
    height: 10px;
    width: 90px;
  }

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    > span {
      width: 64px;
    }
  }
`;

export const StateWrap = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  // A measure, not a width. Translated copy grows into it instead of past it.
  max-width: 46ch;
  text-align: center;
`;

export const StateTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
  margin: 0 0 10px;
  text-wrap: balance;
`;

export const StateBody = styled.p`
  color: ${({ theme }) => theme.colors.shade200};
  font-size: 15px;
  line-height: 1.5;
  margin: 0;
  text-wrap: pretty;
`;

export const StateActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-top: 20px;
`;

// The filled sibling of GhostButton, for the one action worth taking first.
export const PrimaryButton = styled.button`
  background: ${({ theme }) => theme.colors.envy};
  border: 1px solid ${({ theme }) => theme.colors.envy};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.midnight};
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  padding: 8px 16px;
  transition:
    filter 0.15s ease,
    opacity 0.15s ease;

  &:hover:not(:disabled) {
    filter: brightness(1.08);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

// GhostButton centres and top-margins itself for its use inside EmptyState;
// in an actions row the row owns the spacing.
export const StateGhostButton = styled(GhostButton)`
  align-self: auto;
  margin-top: 0;
`;

// --- Loading skeleton -------------------------------------------------------
// The real layout components carry the geometry; these only fill them. Anything
// here that duplicated a width would drift the first time the page changed.

// --- Your Performance -------------------------------------------------------
// The tab's thesis is position, not magnitude: every number here is read
// against the other nine players in the lobby, which the scoreboard cannot do
// for one player without making you scan ten rows and hold them in your head.

// Status for the Match VOD tab, readable before the tab is opened. Entitlement
// deliberately does not change it: a free player sees that the recording exists
// and is told about the plan inside the tab, rather than being shown a locked
// tab and given no reason to look.
export const TabBadge = styled.span<{ $ready: boolean }>`
  background: ${({ $ready, theme }) =>
    $ready ? theme.colors.limeAlpha20 : theme.colors.chalkAlpha10};
  border-radius: 999px;
  color: ${({ $ready, theme }) =>
    $ready ? theme.colors.lime : theme.colors.chalk};
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1;
  padding: 3px 6px;
  text-transform: uppercase;
  white-space: nowrap;
`;

// --- VOD player frame -------------------------------------------------------
// Reads as a video player, not a marketing panel: real frame, real 16:9, a
// centred play button and a control bar. A locked VOD looks exactly the same,
// because the point is that the player already has this recording and only
// needs access to it. The lock is stated in a chip, not by hiding the play.

export const VodPlayer = styled.div<{ $art?: string }>`
  aspect-ratio: 16 / 9;
  background: ${({ $art }) =>
      $art ? `url(${$art}) center / cover no-repeat` : "none"},
    ${({ theme }) => theme.colors.midnight};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 10px;
  overflow: hidden;
  position: relative;

  // Chrome scrims top and bottom, plus the banner's own pool-of-shade device
  // recentred on the play button, so the one thing to press sits in its own
  // pocket of contrast instead of competing with the brightest part of a map.
  &::after {
    background: radial-gradient(
        ellipse 46% 58% at 50% 50%,
        ${({ theme }) => theme.colors.midnightAlpha70} 0%,
        transparent 70%
      ),
      linear-gradient(
        180deg,
        ${({ theme }) => theme.colors.midnightAlpha80} 0%,
        transparent 32%
      ),
      linear-gradient(
        0deg,
        ${({ theme }) => theme.colors.midnightAlpha90} 0%,
        transparent 42%
      );
    content: "";
    inset: 0;
    pointer-events: none;
    position: absolute;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  // The one authored moment on this surface: pointing at the frame eases the
  // fog down, as if the footage is about to resolve, and the play button comes
  // to meet you. Blur is the material the locked state is made of, so moving it
  // says "this is nearly yours" in a way a colour or a shadow could not.
  @media (prefers-reduced-motion: no-preference) {
    [data-vod-art],
    [data-vod-play] {
      transition:
        filter 320ms cubic-bezier(0.16, 1, 0.3, 1),
        transform 320ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    &:hover [data-vod-art="preview"],
    &:focus-within [data-vod-art="preview"] {
      filter: blur(5px) brightness(0.95) grayscale(0.45);
    }

    &:hover [data-vod-art="teaser"],
    &:focus-within [data-vod-art="teaser"] {
      filter: blur(11px) brightness(0.52) grayscale(0.45);
    }

    &:hover [data-vod-play],
    &:focus-within [data-vod-play] {
      transform: translate(-50%, -50%) scale(1.06);
    }
  }
`;

// The match-history treatment, same two layers and the same numbers: the
// player's own map art blurred hard underneath, a real CS2 frame from the
// bucket blurred more gently on top. Together they read as footage sitting
// behind frosted glass rather than as a placeholder. Bleed clears roughly 3x
// the blur radius, or the faded edge lands inside the box as a dark outline.
export const VodTeaserArt = styled.div<{ $art?: string }>`
  background-image: ${({ $art }) => ($art ? `url(${$art})` : "none")};
  background-position: center;
  background-size: cover;
  filter: blur(14px) brightness(0.42) grayscale(1);

  // Doubled to out-specify VodPlayer's "> *" rule, which would otherwise force
  // these back to position: relative and lift them above the scrim.
  && {
    inset: -36px;
    position: absolute;
    z-index: 0;
  }
`;

export const VodPreviewArt = styled.div<{ $art?: string }>`
  background-image: ${({ $art }) => ($art ? `url(${$art})` : "none")};
  background-position: center;
  background-size: cover;
  filter: blur(9px) brightness(0.82) grayscale(1);

  // Doubled to out-specify VodPlayer's "> *" rule, which would otherwise force
  // these back to position: relative and lift them above the scrim.
  && {
    inset: -24px;
    position: absolute;
    z-index: 0;
  }
`;

export const VodPlayerTop = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  left: 0;
  padding: 14px 16px;
  position: absolute;
  right: 0;
  top: 0;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    padding: 10px 12px;
  }
`;

export const VodPlayerTitle = styled.span`
  color: ${({ theme }) => theme.colors.iceAlpha75};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-shadow: 0 1px 4px ${({ theme }) => theme.colors.midnightAlpha90};
`;

export const VodLockChip = styled.span`
  align-items: center;
  background: ${({ theme }) => theme.colors.midnightAlpha80};
  border: 1px solid ${({ theme }) => theme.colors.tierPlatAlpha40};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.ice};
  display: inline-flex;
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  gap: 5px;
  letter-spacing: 0.06em;
  padding: 5px 10px;
  text-transform: uppercase;

  svg {
    color: ${({ theme }) => theme.colors.tierPlat};
  }
`;

// A real play button, present whether or not the VOD is unlocked. Replacing it
// with a padlock was the version that made the tab feel like an advert; this
// one makes the recording feel like it is already there.
export const VodPlayButton = styled.button<{ $locked?: boolean }>`
  align-items: center;
  background: ${({ $locked, theme }) =>
    $locked ? theme.colors.tierPlat : theme.colors.envy};
  border: 0;
  border-radius: 50%;
  // Three rings of its own colour rather than a drop shadow alone: it reads as
  // lit from within, which is what makes it the subject of the frame.
  box-shadow:
    0 0 0 8px
      ${({ $locked, theme }) =>
        $locked ? theme.colors.tierPlatAlpha25 : theme.colors.envyAlpha20},
    0 0 0 18px
      ${({ $locked, theme }) =>
        $locked ? theme.colors.tierPlatAlpha15 : theme.colors.envyAlpha10},
    0 10px 34px ${({ theme }) => theme.colors.midnightAlpha80};
  cursor: pointer;
  display: flex;
  height: 84px;
  justify-content: center;
  left: 50%;
  padding: 0;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 84px;

  // A real icon rather than the CSS-triangle trick: it sits on the baseline
  // properly at this size and needs no optical nudge to look centred.
  svg {
    // Ice on purple, midnight on envy: each is the readable one on its own fill.
    color: ${({ $locked, theme }) =>
      $locked ? theme.colors.ice : theme.colors.midnight};
    margin-left: 3px;
  }

  // Scale lives on the parent's hover so the whole frame reacts as one thing.
  &:hover {
    filter: brightness(1.08);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.ice};
    outline-offset: 6px;
  }

  // A 16:9 frame is only ~200px tall on a phone, where an 84px button ringed to
  // 120px would touch both chrome bars. Smaller button, thinner rings.
  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    box-shadow:
      0 0 0 5px
        ${({ $locked, theme }) =>
          $locked ? theme.colors.tierPlatAlpha25 : theme.colors.envyAlpha20},
      0 0 0 11px
        ${({ $locked, theme }) =>
          $locked ? theme.colors.tierPlatAlpha15 : theme.colors.envyAlpha10},
      0 8px 24px ${({ theme }) => theme.colors.midnightAlpha80};
    height: 56px;
    width: 56px;

    svg {
      height: 28px;
      width: 28px;
    }
  }
`;

export const VodControls = styled.div`
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  left: 0;
  padding: 0 16px 14px;
  position: absolute;
  right: 0;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    gap: 6px;
    padding: 0 12px 10px;
  }
`;

// The round markers double as the scrub track: it is the real shape of the
// match, so it says "this is a recording of your game" better than a fake
// progress bar would.
export const VodScrub = styled.div`
  align-items: flex-end;
  display: flex;
  gap: 2px;
  height: 26px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    height: 16px;
  }
`;

export const VodMetaRow = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.iceAlpha75};
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  font-weight: 500;
  gap: 2px 10px;
  justify-content: space-between;

  // Neither label truncates the other; the round count is the one that matters
  // and it wraps first rather than being clipped.
  > span {
    min-width: 0;
  }

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    font-size: 11px;
  }
`;

// --- Tab intro --------------------------------------------------------------
// Every panel opens the same way: what this is, then one line on why it exists.
// The tab strip names things in 11px chrome, which is enough to navigate by and
// not enough to explain anything, so each panel says it once at full size.
// Title first with no label above it: a kicker over a heading is an eyebrow.

export const TabIntro = styled.header`
  margin-bottom: 20px;
  max-width: 62ch;
`;

export const TabTitle = styled.h1`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
  margin: 0 0 6px;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    font-size: 21px;
  }
`;

export const TabDesc = styled.p`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
`;

// --- VOD timeline -----------------------------------------------------------
// Below the frame rather than inside it, the way the viewer itself lays out:
// video, then the round seeker, then the moments rail. Both rows are the same
// grid, so a moment sits in the column of the round it happened in. Flex with
// `flex: 1 1 0` could not guarantee that once a row held different content.

export const VodTimeline = styled.div`
  margin-top: 14px;
`;

export const VodTrack = styled.div<{ $rounds: number }>`
  align-items: flex-end;
  display: grid;
  gap: 2px;
  grid-template-columns: repeat(${({ $rounds }) => $rounds}, minmax(0, 1fr));
  height: 26px;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    height: 18px;
  }
`;

export const VodMomentRow = styled.div<{ $rounds: number }>`
  align-items: center;
  display: grid;
  gap: 2px;
  grid-template-columns: repeat(${({ $rounds }) => $rounds}, minmax(0, 1fr));
  margin-top: 6px;
  min-height: 20px;
`;

// Sits in its round's column. The weapon icon when the moment has one, so the
// rail says what happened rather than only that something did.
export const VodMomentMark = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  display: flex;
  justify-content: center;
  min-width: 0;

  img {
    height: 14px;
    max-width: 100%;
    object-fit: contain;
  }

  // The fallback when a moment carries no icon.
  &[data-plain]::after {
    background: ${({ theme }) => theme.colors.envy};
    border-radius: 50%;
    content: "";
    height: 6px;
    width: 6px;
  }
`;

export const VodTimelineCaption = styled.div`
  align-items: baseline;
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  gap: 2px 12px;
  justify-content: space-between;
  margin-top: 10px;

  b {
    color: ${({ theme }) => theme.colors.ice};
    font-weight: 700;
  }
`;

// --- Performance identity ---------------------------------------------------
// Whose match this is, stated once at the top. The tab is called "Your
// Performance" for every viewer, so the face and the rank are what actually
// say which player these numbers belong to.

// --- Top three --------------------------------------------------------------
// A podium, not three equal tiles: first place is wider and carries the medal
// colour, so the ranking is legible before a single number is read. Equal cards
// would have made the reader compare three ratings to find out who won.

// One crown on the best player's avatar, rather than 1/2/3 markers down the
// column. Three medals asked the reader to decode a second ranking while they
// were already reading a sorted table; a crown is a single fact, read at a
// glance, and it costs the row no horizontal space at all.
export const AvatarCrown = styled.span`
  color: ${({ theme }) => theme.colors.timelineGold};
  display: flex;
  // Sits on the avatar's top edge, tipped the way a crown sits on a head.
  left: 50%;
  pointer-events: none;
  position: absolute;
  top: -7px;
  transform: translateX(-50%) rotate(-12deg);
  // Above AvatarV2's own border overlay and badge.
  z-index: 3;

  svg {
    filter: drop-shadow(
      0 1px 2px ${({ theme }) => theme.colors.midnightAlpha90}
    );
    height: 13px;
    width: 13px;
  }
`;

// Gives the crown something to anchor to without disturbing the avatar itself.
export const AvatarCrownWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  position: relative;
`;

// --- All Clips tab -------------------------------------------------------

// --- Scoreboard clip indicator -------------------------------------------

// --- All Highlights: the lobby top row -----------------------------------

// --- Moment tile identity -------------------------------------------------

// --- Split spotlight: the match's best highlight --------------------------

// Player card on the left, best play and moments on the right, ending level.
export const PerfLayout = styled.div<{ $card?: boolean }>`
  align-items: stretch;
  display: grid;
  gap: 24px;
  grid-template-columns: ${({ $card }) =>
    $card ? "400px minmax(0, 1fr)" : "minmax(0, 1fr)"};

  > div {
    display: flex;
    flex-direction: column;
    gap: 22px;
    min-width: 0;
  }

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const starDraw = keyframes`
  0% { stroke-dashoffset: 100; }
  60% { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: -100; }
`;

// Bottom row: what this is on the left, what to do on the same line at right.
export const BestPlay = styled.span`
  align-items: flex-end;
  bottom: 28px;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  left: 32px;
  position: absolute;
  right: 28px;
  text-align: left;

  > span {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }

  small {
    align-items: center;
    color: ${({ theme }) => theme.colors.statElite};
    display: inline-flex;
    font-size: 13px;
    font-weight: 700;
    gap: 7px;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  strong {
    font-size: 44px;
    font-weight: 700;
    letter-spacing: 0.01em;
    line-height: 1.05;
    text-transform: uppercase;
  }

  em {
    color: ${({ theme }) => theme.colors.shade200};
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
  }

  // While rendering: a quiet pill where the Clip it button sits, over the faded art.
  > [data-rendering-pill] {
    align-items: center;
    background: ${rgba("#0b1116", 0.6)};
    border-radius: 999px;
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha20};
    display: inline-flex;
    flex: none;
    font-style: normal;
    gap: 10px;
    padding: 8px 16px 8px 10px;
  }

  > [data-rendering-pill] svg {
    height: 26px;
    overflow: visible;
    width: 27px;
  }

  > [data-rendering-pill] path {
    fill: none;
    stroke-linejoin: round;
  }

  > [data-rendering-pill] path:first-child {
    stroke: ${({ theme }) => rgba(theme.colors.chalk, 0.3)};
    stroke-width: 1.6;
  }

  > [data-rendering-pill] path:last-child {
    animation: ${starDraw} 1.8s cubic-bezier(0.65, 0, 0.35, 1) infinite;
    stroke: ${({ theme }) => theme.colors.statElite};
    stroke-dasharray: 100;
    stroke-linecap: round;
    stroke-width: 2;
  }

  > [data-rendering-pill] span {
    display: grid;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.25;
  }

  > [data-rendering-pill] small {
    color: ${({ theme }) => theme.colors.statElite};
    font-size: 12px;
    letter-spacing: 0;
    text-transform: none;
  }

  // On a phone the pill goes above the title so the title keeps the full width.
  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    &:has(> [data-rendering-pill]) {
      align-items: flex-start;
      flex-direction: column-reverse;
      gap: 12px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    > [data-rendering-pill] path:last-child {
      animation: none;
      stroke-dasharray: none;
    }
  }

  > i {
    align-items: center;
    background: ${({ theme }) => theme.colors.envy};
    border-radius: 999px;
    box-shadow: 0 10px 30px -10px ${({ theme }) => rgba(theme.colors.envy, 0.7)};
    display: inline-flex;
    flex: none;
    font-size: 17px;
    font-style: normal;
    font-weight: 700;
    gap: 10px;
    height: 48px;
    padding: 0 22px;
  }

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    bottom: 14px;
    gap: 12px;
    left: 14px;
    right: 14px;

    strong {
      font-size: 26px;
    }

    > i {
      font-size: 14px;
      height: 38px;
      padding: 0 14px;
    }
  }
`;

// Unclipped moments only have map art, so it is blurred and dimmed.
const thumbBackground = (image?: string, dim?: boolean) => css`
  &::before {
    background: ${image ? `url(${image})` : "none"} center / cover no-repeat;
    content: "";
    filter: ${dim ? "brightness(0.78) blur(6px)" : "none"};
    inset: 0;
    position: absolute;
    transform: ${dim ? "scale(1.12)" : "none"};
  }

  &::after {
    background: linear-gradient(
      180deg,
      ${rgba("#0b1116", 0.55)} 0%,
      ${rgba("#0b1116", 0)} 25%,
      ${rgba("#0b1116", 0)} 45%,
      ${rgba("#0b1116", 0.92)} 100%
    );
    content: "";
    inset: 0;
    position: absolute;
  }

  // Children are all absolutely placed; this only lifts them over the art.
  > * {
    z-index: 1;
  }
`;

const cardButton = css`
  background: ${({ theme }) => theme.colors.navy300};
  border: 0;
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: block;
  font: inherit;
  overflow: hidden;
  padding: 0;
  position: relative;
  width: 100%;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: progress;
  }
`;

// While a clip renders: the art drops back and the star loader takes over.
// Shared by the spotlight and the moment cards.
const renderingOverlay = css`
  > [data-rendering] {
    align-items: center;
    background: ${rgba("#0b1116", 0.78)};
    display: flex;
    flex-direction: column;
    gap: 10px;
    inset: 0;
    justify-content: center;
    padding: 24px;
    position: absolute;
    text-align: center;
  }

  [data-rendering] svg {
    height: 56px;
    overflow: visible;
    width: 58px;
  }

  [data-rendering] path {
    fill: none;
    stroke-linejoin: round;
  }

  [data-rendering] path:first-child {
    stroke: ${({ theme }) => rgba(theme.colors.chalk, 0.3)};
    stroke-width: 1.4;
  }

  [data-rendering] path:last-child {
    animation: ${starDraw} 1.8s cubic-bezier(0.65, 0, 0.35, 1) infinite;
    stroke: ${({ theme }) => theme.colors.statElite};
    stroke-dasharray: 100;
    stroke-linecap: round;
    stroke-width: 1.8;
  }

  [data-rendering] strong {
    font-size: 20px;
    font-weight: 700;
  }

  [data-rendering] span {
    color: ${({ theme }) => theme.colors.shade200};
    font-size: 15px;
    line-height: 1.4;
    max-width: 340px;
  }

  [data-rendering] small {
    color: ${({ theme }) => theme.colors.statElite};
    font-size: 14px;
    font-weight: 700;
  }

  @media (prefers-reduced-motion: reduce) {
    [data-rendering] path:last-child {
      animation: none;
      stroke-dasharray: none;
    }
  }
`;

export const BestPlaySlot = styled.div`
  position: relative;
`;

// Share and open sit in the spotlight's top corner, over the card or the video.
export const BestPlayActions = styled.div`
  display: flex;
  gap: 8px;
  position: absolute;
  right: 14px;
  top: 14px;
  z-index: 3;

  > a,
  > button {
    align-items: center;
    backdrop-filter: blur(8px);
    background: ${rgba("#0b1116", 0.55)};
    border: 0;
    border-radius: 50%;
    box-shadow: inset 0 0 0 1px ${({ theme }) => rgba(theme.colors.ice, 0.18)};
    color: ${({ theme }) => theme.colors.ice};
    cursor: pointer;
    display: inline-flex;
    height: 38px;
    justify-content: center;
    transition: background 0.2s ease;
    width: 38px;
  }

  > a:hover,
  > button:hover {
    background: ${rgba("#0b1116", 0.8)};
  }

  > a:focus-visible,
  > button:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

export const BestPlayCard = styled.button<{ $image?: string; $dim?: boolean }>`
  ${cardButton}
  aspect-ratio: 16 / 9;
  ${({ $image, $dim }) => thumbBackground($image, $dim)}

  > [data-cta] {
    align-items: center;
    display: flex;
    inset: 0;
    justify-content: center;
    position: absolute;
  }
  ${renderingOverlay}

  // A phone-height card: taller so the play button clears a two-line title.
  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    aspect-ratio: 1 / 1;
  }
`;

export const MomentsHead = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin: 0 0 12px;

  h2 {
    font-size: 18px;
    font-weight: 700;
    margin: 0;
  }

  button {
    align-items: center;
    background: ${({ theme }) => theme.colors.navy100};
    border: 0;
    border-radius: 999px;
    color: ${({ theme }) => theme.colors.ice};
    cursor: pointer;
    display: inline-flex;
    flex: none;
    font: inherit;
    font-size: 14px;
    font-weight: 700;
    gap: 6px;
    height: 36px;
    padding: 0 14px;
    transition: background 0.15s ease;

    &:hover {
      background: ${({ theme }) => theme.colors.navy50};
    }

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.colors.envy};
      outline-offset: 2px;
    }
  }
`;

export const MomentCard = styled.button<{ $image?: string; $dim?: boolean }>`
  ${cardButton}
  aspect-ratio: 2 / 3;
  border-radius: 10px;
  text-align: left;
  ${({ $image, $dim }) => thumbBackground($image, $dim)}

  // A clipped moment shows its real frame with the play button over it.
  > [data-play] {
    left: 50%;
    position: absolute;
    top: 42%;
    transform: translate(-50%, -50%);
    transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  }

  &:hover > [data-play] {
    transform: translate(-50%, -50%) scale(1.06);
  }

  > [data-preview] {
    height: 100%;
    inset: 0;
    object-fit: cover;
    opacity: 0;
    position: absolute;
    transition: opacity 0.2s ease;
    width: 100%;
  }

  &:hover > [data-preview] {
    opacity: 1;
  }

  > small {
    background: rgba(11, 17, 22, 0.6);
    border-radius: 6px;
    color: ${({ theme }) => theme.colors.shade200};
    font-size: 12px;
    font-weight: 700;
    left: 12px;
    padding: 2px 7px;
    position: absolute;
    top: 12px;
  }

  [data-meta] {
    align-items: center;
    display: flex;
    gap: 8px;
    min-width: 0;
  }

  [data-pips] {
    display: inline-flex;
    flex: none;
    gap: 3px;
  }

  [data-pips] i {
    background: ${({ theme }) => theme.colors.ice};
    border-radius: 2px;
    height: 7px;
    transform: rotate(45deg);
    width: 7px;
  }

  [data-meta] b {
    color: ${({ theme }) => theme.colors.shade200};
    font-size: 13px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  em[data-watch] {
    background: ${({ theme }) => theme.colors.envy};
  }

  &:hover em[data-watch] {
    background: ${({ theme }) => rgba(theme.colors.envy, 0.85)};
  }

  > span {
    bottom: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    left: 12px;
    position: absolute;
    right: 12px;
  }

  strong {
    font-size: 40px;
    font-style: italic;
    font-weight: 900;
    letter-spacing: -0.01em;
    line-height: 0.9;
    text-transform: uppercase;
  }

  em {
    align-items: center;
    background: ${({ theme }) => rgba(theme.colors.ice, 0.16)};
    border-radius: 999px;
    display: flex;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    gap: 6px;
    height: 38px;
    justify-content: center;
    transition: background 0.15s ease;
  }

  &:hover em {
    background: ${({ theme }) => rgba(theme.colors.ice, 0.26)};
  }

  ${renderingOverlay}

  > [data-rendering] {
    gap: 8px;
    padding: 14px;
  }

  [data-rendering] svg {
    height: 40px;
    width: 42px;
  }

  [data-rendering] strong {
    font-size: 16px;
  }

  [data-rendering] span {
    font-size: 13px;
  }
`;

// Tall, clip-shaped cards in a row; on a phone the row swipes sideways.
export const MomentGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, minmax(0, 1fr));

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    grid-auto-columns: 46%;
    grid-auto-flow: column;
    grid-template-columns: none;
    overflow-x: auto;
    padding-bottom: 6px;
    scroll-snap-type: x mandatory;

    > * {
      scroll-snap-align: start;
    }
  }

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    gap: 8px;
    grid-auto-columns: 62%;
  }
`;

// All highlights: one player picker in place of the row of chips.
export const HighlightsMenu = styled.div`
  justify-self: start;
  margin: 12px 0 18px;
  position: relative;
  width: fit-content;

  > button {
    align-items: center;
    background: ${({ theme }) => theme.colors.ned};
    border: 0;
    border-radius: 999px;
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha40};
    color: ${({ theme }) => theme.colors.ice};
    cursor: pointer;
    display: flex;
    font: inherit;
    gap: 10px;
    padding: 6px 12px 6px 6px;
  }

  > button:hover,
  > button[aria-expanded="true"] {
    background: ${({ theme }) => theme.colors.navy200};
  }

  > button b {
    font-size: 16px;
  }

  > button small,
  [role="menu"] small {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
  }

  > button svg {
    color: ${({ theme }) => theme.colors.chalk};
    transition: transform 0.2s ease;
  }

  > button[aria-expanded="true"] svg {
    transform: rotate(180deg);
  }

  img,
  i {
    align-items: center;
    background: ${({ theme }) => theme.colors.navy50};
    border-radius: 50%;
    display: flex;
    flex: none;
    font-size: 12px;
    font-style: normal;
    font-weight: 900;
    height: 30px;
    justify-content: center;
    object-fit: cover;
    width: 30px;
  }

  [role="menu"] {
    background: ${({ theme }) => theme.colors.navy900};
    border-radius: 14px;
    box-shadow:
      0 24px 60px -16px rgba(0, 0, 0, 0.9),
      inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha40};
    display: grid;
    left: 0;
    max-width: calc(100vw - 32px);
    padding: 6px;
    position: absolute;
    top: calc(100% + 8px);
    width: 300px;
    z-index: 10;
  }

  h6 {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 11.5px;
    letter-spacing: 0.8px;
    margin: 8px 10px 4px;
    text-transform: uppercase;
  }

  [role="menuitemradio"] {
    align-items: center;
    background: none;
    border: 0;
    border-radius: 9px;
    color: ${({ theme }) => theme.colors.ice};
    cursor: pointer;
    display: grid;
    font: inherit;
    gap: 10px;
    grid-template-columns: 30px minmax(0, 1fr) auto;
    padding: 7px 10px 7px 6px;
    text-align: left;
  }

  [role="menuitemradio"]:hover,
  [role="menuitemradio"][aria-checked="true"] {
    background: ${({ theme }) => theme.colors.navy200};
  }

  [role="menuitemradio"] b {
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  [data-none] {
    opacity: 0.45;
  }

  button:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

// Who died in a moment, overlapping in kill order; the name shows on hover.
export const Victims = styled.span`
  align-items: center;
  display: inline-flex;

  > span {
    align-items: center;
    background: ${({ theme }) => theme.colors.navy50};
    border-radius: 50%;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.navy900};
    color: ${({ theme }) => theme.colors.ice};
    display: flex;
    font-size: 9px;
    font-weight: 900;
    height: 20px;
    justify-content: center;
    margin-left: -5px;
    position: relative;
    width: 20px;
  }

  > span:first-child {
    margin-left: 0;
  }

  img {
    border-radius: 50%;
    height: 100%;
    object-fit: cover;
    width: 100%;
  }

  i {
    background: ${({ theme }) => theme.colors.ice};
    border-radius: 3px;
    bottom: -4px;
    color: ${({ theme }) => theme.colors.navy900};
    font-size: 7.5px;
    font-style: normal;
    font-weight: 900;
    line-height: 1;
    padding: 1px 2px;
    position: absolute;
    right: -6px;
    z-index: 2;
  }

  > span::after {
    background: ${({ theme }) => theme.colors.navy900};
    border-radius: 7px;
    bottom: calc(100% + 7px);
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha40};
    color: ${({ theme }) => theme.colors.ice};
    content: attr(data-name);
    font-size: 12px;
    font-weight: 700;
    left: 0;
    opacity: 0;
    padding: 4px 8px;
    pointer-events: none;
    position: absolute;
    transition: opacity 0.15s ease;
    white-space: nowrap;
    z-index: 4;
  }

  > span:hover::after {
    opacity: 1;
  }

  &[data-big] > span {
    font-size: 12px;
    height: 30px;
    margin-left: -7px;
    width: 30px;
  }

  &[data-big] > span:first-child {
    margin-left: 0;
  }
`;

export const HighlightsStage = styled.div`
  align-items: start;
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);

  > [data-lead] {
    display: grid;
    gap: 12px;
    min-width: 0;
  }

  // The featured card is the spotlight card; these add its weapon and victims.
  [data-lead] em {
    align-items: center;
    display: flex;
    gap: 8px;
  }

  [data-lead] em img {
    height: 14px;
    width: auto;
  }

  [data-killed] {
    align-items: center;
    color: ${({ theme }) => theme.colors.shade200};
    display: flex;
    flex-wrap: wrap;
    font-size: 14px;
    gap: 12px;
    margin-top: 4px;
  }

  [data-killed] b {
    color: ${({ theme }) => theme.colors.ice};
  }

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const HighlightRunners = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    grid-auto-columns: 72%;
    grid-auto-flow: column;
    grid-template-columns: none;
    overflow-x: auto;
    padding-bottom: 6px;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;

    > * {
      scroll-snap-align: start;
    }
  }
`;

// The moment card, shorter, with who died and the action on one line.
export const HighlightRunner = styled(MomentCard)`
  aspect-ratio: 4 / 3.4;

  > span {
    gap: 6px;
  }

  strong {
    font-size: 28px;
  }

  [data-meta] img {
    height: 13px;
    width: auto;
  }

  > [data-clutch] {
    background: rgba(11, 17, 22, 0.75);
    border-radius: 6px;
    color: ${({ theme }) => theme.colors.statElite};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.6px;
    padding: 3px 7px;
    position: absolute;
    right: 10px;
    text-transform: uppercase;
    top: 10px;
  }

  [data-foot] {
    align-items: center;
    display: flex;
    gap: 8px;
    justify-content: space-between;
    min-height: 30px;
  }

  [data-foot] em {
    height: 30px;
    padding: 0 12px;
  }

  [data-duration] {
    color: ${({ theme }) => theme.colors.shade200};
    font-size: 12.5px;
    font-variant-numeric: tabular-nums;
  }
`;

// Everything after the cards, filtered with them, split by whether it's clipped.
export const HighlightList = styled.div`
  background: ${({ theme }) => theme.colors.ned};
  border-radius: 16px;
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha20};
  display: grid;
  gap: 2px;
  padding: 8px;

  [data-filters] {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 4px 4px 8px;
  }

  [data-filters] button {
    align-items: center;
    background: ${({ theme }) => rgba(theme.colors.ice, 0.06)};
    border: 0;
    border-radius: 999px;
    color: ${({ theme }) => theme.colors.shade200};
    cursor: pointer;
    display: inline-flex;
    font: inherit;
    font-size: 13px;
    font-weight: 700;
    gap: 6px;
    padding: 6px 11px;
    white-space: nowrap;
  }

  [data-filters] button:hover {
    background: ${({ theme }) => rgba(theme.colors.ice, 0.1)};
  }

  [data-filters] button small {
    color: ${({ theme }) => theme.colors.chalk};
    font-weight: 600;
  }

  [data-filters] button[aria-pressed="true"] {
    background: ${({ theme }) => theme.colors.ice};
    color: ${({ theme }) => theme.colors.navy900};
  }

  [data-filters] button[aria-pressed="true"] small {
    color: ${({ theme }) => theme.colors.navy900};
    opacity: 0.6;
  }

  [data-filters] button:disabled {
    cursor: default;
    opacity: 0.35;
  }

  [data-filters] img {
    filter: brightness(0) invert(1);
    height: 12px;
    opacity: 0.85;
    width: auto;
  }

  [data-filters] button[aria-pressed="true"] img {
    filter: brightness(0);
  }

  [data-sep] {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    height: 20px;
    margin: 0 4px;
    width: 1px;
  }

  h4 {
    color: ${({ theme }) => theme.colors.chalk};
    display: flex;
    font-size: 12px;
    justify-content: space-between;
    letter-spacing: 0.8px;
    margin: 8px 10px 6px;
    text-transform: uppercase;
  }

  [data-row] {
    align-items: center;
    background: none;
    border: 0;
    border-radius: 10px;
    color: ${({ theme }) => theme.colors.ice};
    cursor: pointer;
    display: grid;
    font: inherit;
    gap: 12px;
    grid-template-columns: 64px minmax(0, 1fr) auto;
    padding: 7px 10px 7px 7px;
    text-align: left;
  }

  [data-row]:hover {
    background: ${({ theme }) => theme.colors.navy200};
  }

  [data-row]:disabled {
    cursor: progress;
  }

  [data-thumb] {
    align-items: center;
    background: center / cover no-repeat;
    border-radius: 7px;
    display: flex;
    height: 40px;
    justify-content: center;
    overflow: hidden;
    position: relative;
    width: 64px;
  }

  [data-thumb][data-dim]::before {
    backdrop-filter: blur(2px) brightness(0.55) saturate(0.6);
    content: "";
    inset: 0;
    position: absolute;
  }

  [data-thumb] b {
    font-size: 18px;
    font-style: italic;
    font-weight: 900;
    position: relative;
  }

  [data-text] {
    display: grid;
    gap: 1px;
    min-width: 0;
  }

  [data-text] > b {
    font-size: 15px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  [data-text] > span {
    align-items: center;
    color: ${({ theme }) => theme.colors.chalk};
    display: flex;
    font-size: 12.5px;
    gap: 8px;
  }

  [data-text] > span > img {
    filter: brightness(0) invert(1);
    height: 12px;
    opacity: 0.85;
    width: auto;
  }

  [data-text] ${Victims} > span {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.ned};
  }

  [data-row]:hover [data-text] ${Victims} > span {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.navy200};
  }

  [data-row] > em {
    border-radius: 999px;
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 13px;
    font-style: normal;
    font-weight: 700;
    padding: 6px 10px;
    white-space: nowrap;
  }

  [data-row]:hover > em {
    background: ${({ theme }) => rgba(theme.colors.ice, 0.1)};
    color: ${({ theme }) => theme.colors.ice};
  }

  [data-duration] {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 12.5px;
    font-variant-numeric: tabular-nums;
  }

  [data-row] > [data-rendering] {
    align-items: center;
    color: ${({ theme }) => theme.colors.statElite};
    display: inline-flex;
    font-size: 13px;
    font-weight: 700;
    gap: 6px;
  }

  [data-row] > [data-rendering] small {
    display: none;
  }

  [data-row] [data-rendering] svg {
    height: 18px;
    overflow: visible;
    width: 18px;
  }

  [data-row] [data-rendering] path {
    fill: none;
    stroke-linejoin: round;
  }

  [data-row] [data-rendering] path:first-child {
    stroke: ${({ theme }) => rgba(theme.colors.chalk, 0.35)};
    stroke-width: 1.6;
  }

  [data-row] [data-rendering] path:last-child {
    animation: ${starDraw} 1.8s cubic-bezier(0.65, 0, 0.35, 1) infinite;
    stroke: ${({ theme }) => theme.colors.statElite};
    stroke-dasharray: 100;
    stroke-linecap: round;
    stroke-width: 2;
  }

  [data-empty] {
    color: ${({ theme }) => theme.colors.chalk};
    display: grid;
    gap: 10px;
    justify-items: center;
    padding: 26px 12px;
    text-align: center;
  }

  [data-empty] button {
    background: none;
    border: 0;
    color: ${({ theme }) => theme.colors.ice};
    cursor: pointer;
    font: inherit;
    font-weight: 700;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  [data-done] {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 13px;
    margin: 6px 10px;
  }

  button:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    [data-row] [data-rendering] path:last-child {
      animation: none;
      stroke-dasharray: none;
    }
  }
`;

export const HighlightsSkeleton = styled.div`
  aspect-ratio: 16 / 9;
  background: ${({ theme }) => theme.colors.ned};
  border-radius: 12px;
  max-width: 60%;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    max-width: none;
  }
`;

export const BestPlayVideo = styled.div`
  border-radius: 12px;
  overflow: hidden;
`;

// Player card: a foil-rimmed slab, lime to T gold to CT blue.
const SPIN_MS = 1300;

const cardIn = keyframes`
  from { opacity: 0; }
`;
const cometSpin = keyframes`
  0% { opacity: 0; transform: rotate(0deg); }
  12% { opacity: 1; }
  88% { opacity: 1; }
  100% { opacity: 0; transform: rotate(360deg); }
`;
const rimFlash = keyframes`
  0% { opacity: 0; }
  25% { opacity: 0.35; }
  100% { opacity: 0; }
`;

const comet = css`
  background: conic-gradient(
    transparent 0deg,
    transparent 250deg,
    ${rgba("#a6e35c", 0.55)} 300deg,
    ${rgba("#f7fcfd", 0.7)} 330deg,
    ${rgba("#6ea8dc", 0.5)} 345deg,
    transparent 360deg
  );
  content: "";
  inset: -100%;
  position: absolute;
`;

// On arrival the card fades in, one thin light laps the rim and the star fills,
// then the rim settles to the same quiet edge as the rest of the page.
export const PlayerCard = styled.article`
  align-self: stretch;
  animation: ${cardIn} 0.2s ease-out both;
  background: ${({ theme }) => rgba(theme.colors.chalk, 0.16)};
  border-radius: 20px;
  box-shadow: 0 24px 60px -24px rgba(0, 0, 0, 0.8);
  isolation: isolate;
  padding: 2px;
  position: relative;

  > div {
    background: radial-gradient(
        120% 50% at 50% 0%,
        ${({ theme }) => rgba(theme.colors.statElite, 0.1)},
        transparent 62%
      ),
      linear-gradient(180deg, #223440 0%, #1e2e39 46%, #1b2933 100%);
    border-radius: 18px;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    position: relative;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// The sharp lap, clipped to the rim, and the T to CT flash after it.
export const CardRim = styled.span`
  border-radius: 20px;
  inset: 0;
  mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  mask-composite: exclude;
  overflow: hidden;
  opacity: 0.6;
  padding: 1.5px;
  pointer-events: none;
  position: absolute;
  z-index: 1;

  &::before {
    ${comet}
    animation: ${cometSpin} ${SPIN_MS}ms cubic-bezier(0.45, 0, 0.2, 1) 0.15s
      both;
  }

  &::after {
    animation: ${rimFlash} 1.4s cubic-bezier(0.16, 1, 0.3, 1) ${SPIN_MS}ms both;
    background: linear-gradient(158deg, #a6e35c, #d9a54a 55%, #6ea8dc);
    content: "";
    inset: 0;
    position: absolute;
  }

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

// Holds the card's place while the rating comes in after the match itself.
export const RatingLoading = styled.div`
  align-items: center;
  background: #1e2e39;
  border-radius: 20px;
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha20};
  display: flex;
  flex-direction: column;
  gap: 18px;
  justify-content: center;
  min-height: 560px;
  padding: 24px;
  text-align: center;

  svg {
    height: 92px;
    overflow: visible;
    width: 96px;
  }

  path {
    fill: none;
    stroke-linejoin: round;
  }

  path:first-child {
    stroke: ${({ theme }) => rgba(theme.colors.chalk, 0.3)};
    stroke-width: 1.4;
  }

  path:last-child {
    animation: ${starDraw} 1.8s cubic-bezier(0.65, 0, 0.35, 1) infinite;
    stroke: ${({ theme }) => theme.colors.statElite};
    stroke-dasharray: 100;
    stroke-linecap: round;
    stroke-width: 1.8;
  }

  strong {
    font-size: 28px;
    font-weight: 700;
    text-transform: uppercase;
  }

  span {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 15px;
    line-height: 1.4;
    max-width: 280px;
  }

  @media (prefers-reduced-motion: reduce) {
    path:last-child {
      animation: none;
      stroke-dasharray: none;
    }
  }

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    min-height: 320px;
  }
`;

export const CardBanner = styled.div<{ $image: string }>`
  background:
    linear-gradient(180deg, rgba(23, 36, 45, 0) 35%, #1b2a34 100%),
    url(${({ $image }) => $image}) center / cover;
  height: 104px;
`;

export const CardWho = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin-top: -46px;
  padding: 0 24px;
  position: relative;

  > span:first-child {
    background: linear-gradient(
      160deg,
      ${({ theme }) => theme.colors.envy},
      #2c7a5f
    );
    border-radius: 50%;
    box-shadow: 0 10px 24px -8px rgba(0, 0, 0, 0.7);
    padding: 3px;
  }

  > span:first-child img {
    border: 3px solid #1b2a34;
    border-radius: 50%;
    display: block;
    height: 76px;
    object-fit: cover;
    width: 76px;
  }

  p {
    font-size: 30px;
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1;
    margin: 10px 0 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const CardScore = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 24px 4px;

  [data-lab] {
    align-items: center;
    color: ${({ theme }) => theme.colors.shade200};
    display: inline-flex;
    font-size: 15px;
    font-weight: 700;
    gap: 6px;
  }

  [data-lab] button {
    background: none;
    border: 0;
    border-radius: 50%;
    color: ${({ theme }) => theme.colors.chalk};
    cursor: help;
    display: inline-grid;
    padding: 0;
    place-items: center;
  }

  [data-lab] button:hover,
  [data-lab] button:focus-visible {
    color: ${({ theme }) => theme.colors.ice};
  }

  [data-lab] button:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  [data-score] {
    align-items: center;
    display: flex;
    gap: 10px;
  }

  b {
    font-size: 96px;
    font-style: italic;
    font-variant-numeric: tabular-nums;
    font-weight: 900;
    letter-spacing: 0.01em;
    line-height: 0.86;
  }

  em {
    align-self: flex-start;
    border-radius: 6px;
    font-size: 13px;
    font-style: normal;
    font-weight: 700;
    letter-spacing: 0.4px;
    margin-top: 8px;
    padding: 3px 8px;
    text-transform: uppercase;
  }

  p {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 14px;
    font-weight: 500;
    margin: 2px 0 0;
    text-align: center;
  }

  p strong {
    color: ${({ theme }) => theme.colors.ice};
  }

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    b {
      font-size: 80px;
    }
  }
`;

// Six segments sized to the rank bands: 30, 20, 15, 15, 12 and 8 points wide.
const BANDS = "30fr 20fr 15fr 15fr 12fr 8fr";

export const CardScale = styled.div`
  margin: 0 24px 18px;

  [data-ladder],
  [data-ladder-names] {
    display: grid;
    gap: 3px;
    grid-template-columns: ${BANDS};
  }

  [data-ladder] {
    margin-top: 22px;
    position: relative;
  }

  [data-ladder] > span {
    background: ${({ theme }) => rgba(theme.colors.ice, 0.1)};
    border-radius: 999px;
    height: 8px;
    position: relative;
  }

  [data-ladder] > [data-past] {
    background: var(--rank);
    opacity: 0.35;
  }

  [data-ladder] > [data-on] {
    background: var(--rank);
    box-shadow: 0 0 12px var(--rank);
  }

  // The rank's name, shown under its segment on hover.
  [data-ladder] > span::after {
    background: #0f181e;
    border-radius: 6px;
    box-shadow: 0 8px 20px -8px rgba(0, 0, 0, 0.8);
    color: ${({ theme }) => theme.colors.ice};
    content: attr(data-name);
    font-size: 12px;
    font-weight: 700;
    left: 50%;
    opacity: 0;
    padding: 3px 8px;
    pointer-events: none;
    position: absolute;
    top: calc(100% + 8px);
    transform: translate(-50%, -3px);
    transition:
      opacity 0.15s ease,
      transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
    white-space: nowrap;
    z-index: 2;
  }

  [data-ladder] > span:not([data-on]):hover::after {
    opacity: 1;
    transform: translate(-50%, 0);
  }

  [data-mark] {
    background: ${({ theme }) => theme.colors.ice};
    border-radius: 1px;
    height: 20px;
    position: absolute;
    top: -6px;
    transform: translateX(-50%);
    width: 2px;
  }

  [data-ladder-names] {
    font-size: 12px;
    font-weight: 700;
    margin-top: 8px;
    min-height: 16px;
  }

  [data-ladder-names] > [data-on] {
    color: var(--rank);
    text-align: center;
    white-space: nowrap;
  }

  [data-key] {
    color: ${({ theme }) => theme.colors.shade200};
    display: flex;
    flex-wrap: wrap;
    font-size: 13px;
    font-weight: 700;
    gap: 6px 18px;
    justify-content: center;
    margin-top: 12px;
  }

  [data-key] > span {
    align-items: center;
    display: inline-flex;
    gap: 6px;
  }

  [data-key] > span:last-child {
    color: #b9a6ff;
  }

  [data-key] svg {
    height: 15px;
    width: 15px;
  }

  [data-key] b {
    font-size: 17px;
    font-style: italic;
    font-weight: 900;
  }

  @media (prefers-reduced-motion: reduce) {
    [data-ladder] > span::after {
      transition: none;
    }
  }
`;

export const CardStats = styled.section`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 14px 22px 22px;

  h2 {
    font-size: 16px;
    font-weight: 700;
    margin: 0 0 12px;
  }

  [data-tiles] {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    border-radius: 14px;
    display: grid;
    flex: 1;
    gap: 1px;
    grid-auto-rows: minmax(92px, 1fr);
    grid-template-columns: 1fr 1fr;
    list-style: none;
    margin: 0;
    overflow: hidden;
    padding: 0;
  }

  [data-tiles] li {
    align-content: center;
    background: #182630;
    display: grid;
    gap: 4px;
    outline: none;
    cursor: pointer;
    padding: 14px 16px;
  }

  [data-tiles] li:focus-visible {
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.envy};
  }

  [data-tiles] [data-lead] {
    grid-column: 1 / -1;
  }

  [data-tiles] span {
    align-items: center;
    color: ${({ theme }) => theme.colors.chalk};
    display: flex;
    flex-wrap: wrap;
    font-size: 13px;
    font-weight: 700;
    gap: 4px 8px;
  }

  [data-tiles] small {
    align-items: center;
    color: ${({ theme }) => theme.colors.chalk};
    display: inline-flex;
    font-size: 11px;
    gap: 4px;
    letter-spacing: 0.3px;
  }

  [data-tiles] small svg {
    color: ${({ theme }) => theme.colors.statLeader};
  }

  // An even count leaves the last tile alone on its row, so it spans it.
  [data-tiles] li:last-child:nth-child(even) {
    grid-column: 1 / -1;
  }

  [data-work] {
    display: grid;
    gap: 8px;
    margin-top: 16px;
  }

  [data-work] h4 {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.8px;
    margin: 0;
    text-transform: uppercase;
  }

  [data-work] ul {
    display: grid;
    gap: 1px;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  [data-work] button {
    align-items: baseline;
    background: none;
    border: 0;
    border-radius: 8px;
    color: ${({ theme }) => theme.colors.ice};
    cursor: pointer;
    display: grid;
    font: inherit;
    gap: 2px 12px;
    grid-template-columns: 1fr auto;
    padding: 8px 4px;
    text-align: left;
    width: 100%;
  }

  [data-work] li + li button {
    border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
    border-radius: 0;
  }

  [data-work] button:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  [data-work] span {
    font-size: 14px;
    font-weight: 600;
  }

  [data-work] b {
    font-size: 18px;
    font-style: italic;
    font-variant-numeric: tabular-nums;
    font-weight: 900;
    text-align: right;
  }

  [data-work] em {
    color: ${({ theme }) => theme.colors.statWeak};
    font-size: 12.5px;
    font-style: normal;
    font-weight: 600;
    grid-column: 1 / -1;
  }

  [data-tiles] b {
    color: ${({ theme }) => theme.colors.ice};
    font-size: 30px;
    font-style: italic;
    font-variant-numeric: tabular-nums;
    font-weight: 900;
    line-height: 0.95;
  }

  [data-tiles] [data-lead] b {
    font-size: 48px;
  }

  [data-tiles] em {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 12.5px;
    font-style: normal;
    font-weight: 600;
  }

  [data-tiles] em[data-better] {
    color: ${({ theme }) => theme.colors.statElite};
    font-weight: 700;
  }
`;

const swapReveal = (background: string) => css`
  align-items: center;
  background: ${background};
  border: 0;
  border-radius: 8px;
  box-shadow: -10px 0 10px ${background};
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  height: 30px;
  justify-content: center;
  opacity: 0;
  padding: 0;
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  transition: opacity 0.12s ease;
  width: 30px;

  th:hover &,
  th:focus-within &,
  &[aria-expanded="true"] {
    opacity: 1;
  }

  &:hover,
  &[aria-expanded="true"] {
    color: ${({ theme }) => theme.colors.ice};
  }

  // No hover on touch screens: always show it.
  @media (hover: none) {
    opacity: 1;
  }
`;

// The name sorts and fills the cell; the swap icon appears over the cell's
// right edge on hover or focus, so it never costs the label any width.
export const ColumnHead = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 100%;

  > [data-swap] {
    ${swapReveal("#1a2833")}
  }
`;

// A dropdown on desktop, a bottom sheet on a phone.
export const ColumnMenu = styled.div<{ $floating?: boolean }>`
  background: ${({ theme }) => theme.colors.navy400};
  border-radius: 12px;
  box-shadow:
    0 16px 40px rgba(0, 0, 0, 0.5),
    inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha20};
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 420px;
  padding: 8px;
  position: absolute;
  right: 0;
  text-align: left;
  top: calc(100% + 8px);
  width: 260px;
  z-index: 20;

  ${({ $floating }) =>
    $floating &&
    css`
      left: var(--left);
      position: fixed;
      right: auto;
      top: var(--top);
      z-index: 1000;
    `}

  [data-sheet-head] {
    display: none;
  }

  input {
    background: ${({ theme }) => theme.colors.navy600};
    border: 0;
    border-radius: 8px;
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha20};
    color: ${({ theme }) => theme.colors.ice};
    font: inherit;
    font-size: 14px;
    padding: 8px 10px;
  }

  [role="listbox"] {
    overflow-y: auto;
  }

  small {
    color: ${({ theme }) => theme.colors.chalk};
    display: block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 10px 8px 4px;
    text-transform: uppercase;
  }

  button {
    align-items: center;
    background: none;
    border: 0;
    border-radius: 6px;
    color: ${({ theme }) => theme.colors.ice};
    cursor: pointer;
    display: flex;
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    justify-content: space-between;
    padding: 8px;
    text-transform: none;
    width: 100%;
  }

  button:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.navyAlpha50};
  }

  button:disabled {
    cursor: default;
    opacity: 0.45;
  }

  button em {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 12px;
    font-style: normal;
  }

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    border-radius: 16px 16px 0 0;
    bottom: 0;
    left: 0;
    max-height: 70vh;
    position: fixed;
    right: 0;
    top: auto;
    width: auto;

    [data-sheet-head] {
      align-items: center;
      display: flex;
      justify-content: space-between;
      padding: 4px 8px;
    }

    [data-sheet-head] button {
      color: ${({ theme }) => theme.colors.envy};
      font-weight: 700;
      width: auto;
    }
  }
`;

const SB_RATING_BG = "#243644";
// Its own hue: green, lime, amber and coral already mean something here.
const PLAYED_WITH = "#9d8cff";

export const SbPage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  > section {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
`;

// Inset to match the table's text: 4px frame padding plus 14px cell padding.
export const SbToolbar = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  justify-content: space-between;
  padding: 0 18px;
`;

export const SbPresets = styled.div`
  align-items: center;
  display: flex;
  font-size: 15px;
  gap: 18px;

  > span {
    color: ${({ theme }) => theme.colors.chalk};
    font-weight: 500;
    margin-right: 2px;
  }

  button {
    background: none;
    border: 0;
    color: ${({ theme }) => theme.colors.chalk};
    cursor: pointer;
    font: inherit;
    font-weight: 700;
    padding: 0;
    transition: color 0.15s ease;
  }

  button:hover,
  button[aria-checked="true"] {
    color: ${({ theme }) => theme.colors.ice};
  }

  button:focus-visible {
    border-radius: 2px;
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 3px;
  }
`;

export const SbTeamCell = styled.th<{ $won?: boolean }>`
  && {
    font-size: 16px;
  }

  b {
    color: ${({ $won, theme }) =>
      $won ? theme.colors.envy : theme.colors.chalk};
    font-size: 22px;
    font-style: italic;
    font-variant-numeric: tabular-nums;
    font-weight: 900;
    letter-spacing: 0.03em;
    margin-right: 12px;
    vertical-align: -2px;
  }

  em {
    border-radius: 6px;
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    letter-spacing: 0.6px;
    margin-left: 10px;
    padding: 3px 8px;
    text-transform: uppercase;
    vertical-align: 1px;
  }

  em[data-result="Victory"] {
    background: ${({ theme }) => rgba(theme.colors.envy, 0.14)};
    color: ${({ theme }) => theme.colors.envy};
  }

  em[data-result="Defeat"] {
    background: ${({ theme }) => rgba(theme.colors.statPoor, 0.14)};
    color: ${({ theme }) => theme.colors.statPoor};
  }

  em[data-result="Draw"] {
    background: ${({ theme }) => theme.colors.iceAlpha08};
    color: ${({ theme }) => theme.colors.shade200};
  }
`;

export const SbAvg = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.shade200};
  display: inline-flex;
  font-weight: 500;
  gap: 8px;
`;

export const SbFrame = styled.div`
  background: #1e2e3a;
  border-radius: 10px;
  overflow-x: auto;
  padding: 4px;
`;

export const SbTable = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  font-size: 16px;
  font-variant-numeric: tabular-nums;
  min-width: 980px;
  table-layout: fixed;
  width: 100%;

  th,
  td {
    padding: 8px 14px;
    text-align: center;
    white-space: nowrap;
  }

  th {
    background: #1a2833;
    color: ${({ theme }) => theme.colors.ice};
    font-size: 14px;
    font-weight: 700;
    height: 42px;
    position: relative;
  }

  th:first-child {
    border-radius: 8px 0 0 8px;
    text-align: left;
  }

  th:last-child {
    border-radius: 0 8px 8px 0;
  }

  th:nth-child(2) {
    background: ${SB_RATING_BG};
    padding: 0;
    position: relative;
  }

  // Same mark, same size as the rating card's label.
  th:nth-child(2) svg {
    height: 13px;
    width: 8px;
  }

  // Numbers have to read at a glance: a size up, full white, a little air.
  td {
    color: ${({ theme }) => theme.colors.ice};
    font-size: 17px;
    font-weight: 500;
    height: 48px;
    letter-spacing: 0.02em;
  }

  tbody tr + tr td {
    border-top: 1px solid ${({ theme }) => rgba(theme.colors.chalk, 0.08)};
  }

  td:first-child {
    text-align: left;
  }

  td:nth-child(2) {
    background: ${SB_RATING_BG};
  }
`;

export const SbRow = styled.tr<{ $viewer?: boolean; $mine?: boolean }>`
  background: ${({ $viewer, theme }) =>
    $viewer ? rgba(theme.colors.envy, 0.05) : "transparent"};

  td:first-child {
    background: ${({ $mine, theme }) =>
      $mine ? rgba(theme.colors.envy, 0.07) : rgba(theme.colors.chalk, 0.06)};
  }

  // Players you regularly play with: one violet stripe down their rows.
  &[data-stack] td:first-child {
    background: linear-gradient(90deg, ${PLAYED_WITH} 0 4px, transparent 4px),
      ${({ $mine, theme }) =>
        $mine ? rgba(theme.colors.envy, 0.07) : rgba(theme.colors.chalk, 0.06)};
  }

  &[data-stack] + &[data-stack] td:first-child {
    border-top-color: transparent;
  }
`;

export const SbPlayer = styled(Link)`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  display: flex;
  gap: 12px;
  min-width: 0;
  text-decoration: none;

  // Long names would otherwise squeeze the avatar and shift every name's start.
  > :first-child {
    flex: none;
  }

  > span:nth-child(2) {
    font-size: 16px;
    font-weight: 700;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // A fixed slot, so a rank change never moves the badge.
  > :last-child {
    flex: none;
    justify-content: flex-start;
    margin-left: auto;
    width: 104px;
  }

  &:hover > span:nth-child(2) {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

export const SbRating = styled.span<{ $tone: TStatTone }>`
  align-items: center;
  color: ${({ $tone, theme }) => theme.colors[TONE_COLOR[$tone]]};
  display: inline-flex;
  font-size: 18px;
  font-style: italic;
  letter-spacing: 0.03em;
  font-weight: 900;
  gap: 10px;
`;

export const SbSortButton = styled.button`
  align-items: center;
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  gap: 6px;
  justify-content: center;
  padding: 0;

  > span {
    color: ${({ theme }) => theme.colors.chalkAlpha60};
    display: inline-flex;
  }

  > span[data-on],
  &:hover > span {
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

// Fills the Rating header cell: anywhere on it sorts, the icon switches sides.
export const SbRatingHead = styled.div`
  align-items: stretch;
  display: flex;
  inset: 0;
  position: absolute;

  > ${SbSortButton} {
    flex: 1;
  }

  > [data-swap] {
    ${swapReveal(SB_RATING_BG)}
  }
`;

export const SbStat = styled.span<{ $tone: TStatTone }>`
  color: ${({ $tone, theme }) => theme.colors[TONE_COLOR[$tone]]};

  // The match's best value in this column.
  &[data-best] {
    align-items: center;
    color: ${({ theme }) => theme.colors.statLeader};
    display: inline-flex;
    font-weight: 700;
    gap: 4px;
  }

  &[data-best] svg {
    flex: none;
    height: 10px;
    width: 10px;
  }
`;

export const SbKda = styled.span`
  display: inline-flex;
  gap: 4px;

  b {
    font-weight: 500;
  }

  i {
    color: #4f6670;
    font-style: normal;
  }
`;

export const StatHint = styled.div`
  background: ${({ theme }) => theme.colors.navy400};
  border-radius: 12px;
  box-shadow:
    0 16px 40px rgba(0, 0, 0, 0.5),
    inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha20};
  color: ${({ theme }) => theme.colors.shade200};
  display: flex;
  flex-direction: column;
  font-size: 14px;
  font-weight: 500;
  gap: 6px;
  left: var(--left);
  line-height: 1.45;
  padding: 12px 14px;
  pointer-events: none;
  position: fixed;
  text-align: left;
  text-transform: none;
  top: var(--top);
  white-space: normal;
  width: 280px;
  z-index: 1000;

  strong {
    color: ${({ theme }) => theme.colors.ice};
    font-size: 15px;
    font-weight: 700;
  }

  span + strong {
    margin-top: 8px;
  }

  small {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 13px;
    margin-top: 4px;
  }
`;

export const WinHeadline = styled.div`
  margin: 0 0 20px;
  max-width: 760px;

  h2 {
    font-size: 34px;
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1.08;
    margin: 0 0 10px;
  }

  p {
    color: ${({ theme }) => theme.colors.shade200};
    font-size: 17px;
    line-height: 1.5;
    margin: 0;
  }

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    h2 {
      font-size: 24px;
    }

    p {
      font-size: 15px;
    }
  }
`;

// The match chart is the page: chapters live in it as bands. On a phone it
// keeps its proportions and scrolls sideways rather than squashing.
export const WinChart = styled.div`
  background: #1b2933;
  border-radius: 18px;
  box-shadow:
    inset 0 0 0 1px ${({ theme }) => rgba(theme.colors.chalk, 0.12)},
    0 24px 60px -30px rgba(0, 0, 0, 0.8);
  overflow-x: auto;
  position: relative;

  > svg {
    display: block;
    min-width: 760px;
    width: 100%;
  }

  > svg:focus-visible {
    border-radius: 18px;
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  [data-tint="CT"] {
    fill: ${rgba("#6ea8dc", 0.06)};
  }

  [data-tint="T"] {
    fill: ${rgba("#d9a54a", 0.06)};
  }

  [data-grid] {
    stroke: ${({ theme }) => rgba(theme.colors.chalk, 0.12)};
  }

  [data-axis] {
    fill: ${({ theme }) => theme.colors.chalk};
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    font-weight: 500;
  }

  [data-side] {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
  }

  [data-side="CT"] {
    fill: #6ea8dc;
  }

  [data-side="T"] {
    fill: #d9a54a;
  }

  [data-guide] {
    stroke: ${({ theme }) => rgba(theme.colors.statElite, 0.55)};
    stroke-dasharray: 3 4;
  }

  [data-point] {
    stroke: #1b2933;
    stroke-width: 2;
  }

  [data-low] {
    fill: none;
    opacity: 0.7;
    stroke: ${({ theme }) => theme.colors.statPoor};
    stroke-width: 1.5;
  }

  [data-low][data-high] {
    stroke: ${({ theme }) => theme.colors.statElite};
  }

  [data-low-value][data-high] {
    fill: ${({ theme }) => theme.colors.statElite};
  }

  [data-low-value] {
    fill: ${({ theme }) => theme.colors.statPoor};
    font-size: 24px;
    font-style: italic;
    font-weight: 900;
    letter-spacing: 0.03em;
  }

  [data-end] {
    fill: ${({ theme }) => theme.colors.ice};
    font-size: 14px;
    font-weight: 700;
  }
`;

export const WinControls = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 0 0 14px;

  [data-swap-view] {
    align-items: center;
    background: ${({ theme }) => theme.colors.iceAlpha08};
    border: 0;
    border-radius: 999px;
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha20};
    color: ${({ theme }) => theme.colors.ice};
    cursor: pointer;
    display: inline-flex;
    font: inherit;
    font-size: 14px;
    font-weight: 700;
    gap: 8px;
    height: 36px;
    padding: 0 14px;
    transition: background 0.15s ease;
  }

  [data-swap-view]:hover {
    background: ${({ theme }) => rgba(theme.colors.ice, 0.14)};
  }

  [data-swap-view]:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

export const WinTip = styled.div`
  background: #1e2e3a;
  border-radius: 14px;
  box-shadow:
    0 18px 40px -12px rgba(0, 0, 0, 0.7),
    inset 0 0 0 1px ${({ theme }) => rgba(theme.colors.chalk, 0.18)};
  padding: 14px 16px;
  pointer-events: none;
  position: absolute;
  top: 76px;
  width: 372px;
  z-index: 2;

  [data-head] {
    align-items: center;
    display: flex;
    font-size: 16px;
    gap: 10px;
  }

  [data-chip] {
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.6px;
    padding: 3px 8px;
  }

  [data-side-chip] {
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    padding: 2px 6px;
  }

  [data-side-chip="CT"] {
    background: ${rgba("#6ea8dc", 0.16)};
    color: #6ea8dc;
  }

  [data-side-chip="T"] {
    background: ${rgba("#d9a54a", 0.16)};
    color: #d9a54a;
  }

  [data-chip="won"] {
    background: ${({ theme }) => rgba(theme.colors.statElite, 0.15)};
    color: ${({ theme }) => theme.colors.statElite};
  }

  [data-chip="lost"] {
    background: ${({ theme }) => rgba(theme.colors.statPoor, 0.14)};
    color: ${({ theme }) => theme.colors.statPoor};
  }

  [data-score] {
    color: ${({ theme }) => theme.colors.chalk};
    font-variant-numeric: tabular-nums;
    margin-left: auto;
  }

  [data-score] em {
    color: #d9a54a;
    font-style: normal;
    font-weight: 700;
  }

  p {
    color: ${({ theme }) => theme.colors.shade200};
    font-size: 14px;
    line-height: 1.45;
    margin: 10px 0 0;
  }

  [data-effect] {
    color: ${({ theme }) => theme.colors.chalk};
    margin-bottom: 10px;
  }

  [data-moment] {
    align-items: center;
    background: ${({ theme }) => rgba(theme.colors.ice, 0.04)};
    border-radius: 8px;
    color: ${({ theme }) => theme.colors.shade200};
    display: grid;
    font-size: 13px;
    gap: 8px;
    grid-template-columns: 70px 1fr 52px;
    margin-top: 6px;
    padding: 6px 8px;
  }

  [data-clock] {
    color: ${({ theme }) => theme.colors.chalk};
    font-variant-numeric: tabular-nums;
  }

  // Kill feed: killer, weapon, victim.
  [data-feed] {
    align-items: center;
    display: flex;
    gap: 10px;
    min-width: 0;
  }

  [data-avatar] {
    align-items: center;
    background: #3f6488;
    border-radius: 50%;
    display: flex;
    flex: none;
    font-size: 11px;
    font-weight: 700;
    height: 26px;
    justify-content: center;
    object-fit: cover;
    width: 26px;
  }

  [data-avatar][data-you] {
    box-shadow: 0 0 0 2px #d9a54a;
  }

  [data-weapon] {
    height: 16px;
    opacity: 0.85;
  }

  [data-killed] {
    color: ${({ theme }) => theme.colors.chalk};
  }

  [data-text] {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  b {
    color: ${({ theme }) => theme.colors.shade200};
    font-weight: 500;
  }

  b[data-mate] {
    color: ${({ theme }) => theme.colors.ice};
  }

  b[data-you] {
    color: #d9a54a;
    font-weight: 700;
  }

  [data-swing] {
    align-items: center;
    display: inline-flex;
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    gap: 4px;
    justify-content: flex-end;
  }

  [data-swing="up"] {
    color: ${({ theme }) => theme.colors.statElite};
  }

  [data-swing="down"] {
    color: ${({ theme }) => theme.colors.statPoor};
  }
`;

export const WinMoments = styled.section`
  border-top: 1px solid ${({ theme }) => rgba(theme.colors.chalk, 0.14)};
  margin-top: 48px;
  padding-top: 28px;

  [data-heading] {
    align-items: baseline;
    display: flex;
    flex-wrap: wrap;
    gap: 8px 24px;
    margin-bottom: 18px;
  }

  h3 {
    font-size: 22px;
    font-weight: 700;
    margin: 0;
  }

  [data-heading] p {
    color: ${({ theme }) => theme.colors.shade200};
    font-size: 16px;
    margin: 0;
  }

  [data-heading] b {
    color: ${({ theme }) => theme.colors.statElite};
    font-size: 20px;
    font-style: italic;
    font-weight: 900;
    letter-spacing: 0.03em;
  }

  [data-row] {
    align-items: center;
    color: ${({ theme }) => theme.colors.shade200};
    display: grid;
    font-size: 15px;
    gap: 20px;
    grid-template-columns: 90px 1fr 300px 120px;
    padding: 14px 0;
  }

  [data-row] + [data-row] {
    border-top: 1px solid ${({ theme }) => rgba(theme.colors.chalk, 0.12)};
  }

  [data-labels] {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 13px;
    padding: 0 0 8px;
  }

  [data-round] {
    color: ${({ theme }) => theme.colors.chalk};
    font-variant-numeric: tabular-nums;
    font-weight: 700;
  }

  [data-what] b {
    color: ${({ theme }) => theme.colors.ice};
    font-weight: 500;
  }

  [data-what] img {
    height: 14px;
    margin: 0 8px;
    opacity: 0.8;
    vertical-align: -2px;
  }

  [data-what] em {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 13px;
    font-style: normal;
  }

  [data-bar] {
    background: ${({ theme }) => theme.colors.iceAlpha08};
    border-radius: 999px;
    height: 8px;
    position: relative;
  }

  [data-range] {
    border-radius: 999px;
    bottom: 0;
    opacity: 0.55;
    position: absolute;
    top: 0;
  }

  [data-from] {
    background: ${({ theme }) => theme.colors.chalk};
    border-radius: 1px;
    height: 16px;
    margin-left: -1px;
    position: absolute;
    top: -4px;
    width: 2px;
  }

  [data-to] {
    border-radius: 5px;
    box-shadow: 0 0 0 2px #131d25;
    height: 18px;
    margin-left: -7px;
    position: absolute;
    top: -5px;
    width: 14px;
  }

  [data-change] {
    color: ${({ theme }) => theme.colors.chalk};
    font-variant-numeric: tabular-nums;
    text-align: right;
  }

  [data-change] b {
    font-weight: 700;
  }

  // On a phone each moment stacks: what happened, then its bar and change.
  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    [data-row] {
      gap: 8px 14px;
      grid-template-columns: 1fr auto;
    }

    [data-labels] {
      display: none;
    }

    [data-round] {
      grid-column: 1 / -1;
    }

    [data-what] {
      grid-column: 1 / -1;
    }
  }
`;

const lineIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
`;

// The match is loading: one card, centered, with the star the rating card
// draws while it waits and a line that changes every few seconds.
export const MatchLoader = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 62vh;
  padding: 24px 0;

  > div {
    align-items: center;
    background: #17242d;
    border-radius: 20px;
    box-shadow:
      inset 0 0 0 1px ${({ theme }) => rgba(theme.colors.chalk, 0.14)},
      0 30px 70px -34px rgba(0, 0, 0, 0.85);
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-width: 100%;
    padding: 40px 36px 34px;
    text-align: center;
    width: 420px;
  }

  svg {
    height: 64px;
    margin-bottom: 12px;
    overflow: visible;
    width: 66px;
  }

  path {
    fill: none;
    stroke-linejoin: round;
  }

  path:first-child {
    stroke: ${({ theme }) => rgba(theme.colors.chalk, 0.3)};
    stroke-width: 1.4;
  }

  path:last-child {
    animation: ${starDraw} 1.8s cubic-bezier(0.65, 0, 0.35, 1) infinite;
    stroke: ${({ theme }) => theme.colors.statElite};
    stroke-dasharray: 100;
    stroke-linecap: round;
    stroke-width: 1.8;
  }

  strong {
    font-size: 22px;
    font-weight: 700;
  }

  // Fixed height so the card never jumps when a longer line comes in.
  span {
    animation: ${lineIn} 0.35s cubic-bezier(0.16, 1, 0.3, 1);
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 15px;
    line-height: 1.4;
    min-height: 42px;
  }

  @media (prefers-reduced-motion: reduce) {
    path:last-child {
      animation: none;
      stroke-dasharray: none;
    }

    span {
      animation: none;
    }
  }
`;

// Allstar Rating tab: the band says how the rating was built, the lens shows one breakdown at a time.
const rbSwitch = css`
  [data-switch] {
    background: rgba(11, 17, 22, 0.5);
    border-radius: 12px;
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha20};
    display: inline-flex;
    gap: 2px;
    padding: 3px;
  }

  [data-switch] button {
    align-items: center;
    background: none;
    border: 0;
    border-radius: 9px;
    color: ${({ theme }) => theme.colors.chalk};
    cursor: pointer;
    display: inline-flex;
    font: inherit;
    font-size: 14px;
    font-weight: 700;
    gap: 7px;
    padding: 7px 13px;
    white-space: nowrap;
  }

  [data-switch] button svg {
    height: 15px;
    width: 15px;
  }

  [data-switch] button[aria-pressed="true"] {
    background: ${({ theme }) => rgba(theme.colors.ice, 0.1)};
    color: ${({ theme }) => theme.colors.ice};
  }

  [data-switch] button[data-lens="style"][aria-pressed="true"] {
    color: #b9a6ff;
  }

  [data-switch] button:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

const rbInfo = css`
  [data-info] {
    background: none;
    border: 0;
    border-radius: 50%;
    color: ${({ theme }) => theme.colors.chalk};
    cursor: help;
    display: inline-grid;
    margin-left: 6px;
    padding: 0;
    place-items: center;
    vertical-align: -3px;
  }

  [data-info]:hover,
  [data-info]:focus-visible {
    color: ${({ theme }) => theme.colors.ice};
  }

  [data-info]:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

export const RbBand = styled.section<{ $image: string }>`
  border-radius: 22px;
  box-shadow:
    inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha20},
    0 30px 70px -40px rgba(0, 0, 0, 0.9);
  display: grid;
  gap: 24px 56px;
  grid-template-columns: auto minmax(0, 1fr);
  isolation: isolate;
  margin-bottom: 20px;
  padding: 28px 34px 30px;
  position: relative;

  // The banner sits behind a navy wash so the numbers stay readable.
  &::before,
  &::after {
    border-radius: inherit;
    content: "";
    inset: 0;
    position: absolute;
  }

  &::before {
    background: url(${({ $image }) => $image}) center 30% / cover;
    z-index: -2;
  }

  &::after {
    background: linear-gradient(
        90deg,
        rgba(19, 29, 37, 0.96) 0%,
        rgba(19, 29, 37, 0.9) 42%,
        rgba(19, 29, 37, 0.72) 100%
      ),
      linear-gradient(180deg, rgba(19, 29, 37, 0) 40%, rgba(19, 29, 37, 0.9));
    z-index: -1;
  }

  ${rbSwitch}
  ${rbInfo}

  [data-head] {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 12px 20px;
    grid-column: 1 / -1;
    justify-content: space-between;
  }

  h2 {
    color: ${({ theme }) => theme.colors.shade200};
    font-size: 18px;
    font-weight: 700;
    margin: 0;
    text-wrap: balance;
  }

  [data-switch] button {
    font-size: 13px;
    padding: 6px 12px;
  }

  [data-score] {
    align-items: center;
    display: flex;
    gap: 14px;
  }

  [data-score] > b {
    font-size: 132px;
    font-style: italic;
    font-variant-numeric: tabular-nums;
    font-weight: 900;
    letter-spacing: 0.01em;
    line-height: 0.86;
  }

  [data-score] > em {
    align-self: flex-start;
    border-radius: 7px;
    font-size: 13px;
    font-style: normal;
    font-weight: 700;
    letter-spacing: 0.4px;
    margin-top: 12px;
    padding: 4px 9px;
    text-transform: uppercase;
  }

  [data-parts] {
    align-items: end;
    align-self: center;
    display: grid;
    gap: 18px;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    max-width: 560px;
  }

  [data-plus] {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 30px;
    font-weight: 700;
    padding-bottom: 22px;
  }

  [data-part] {
    display: grid;
    gap: 8px;
  }

  [data-part-label] {
    color: ${({ theme }) => theme.colors.shade200};
    font-size: 15px;
    font-weight: 700;
  }

  [data-part-value] {
    align-items: center;
    display: flex;
    gap: 10px;
  }

  [data-part-value] > svg {
    height: 26px;
    width: 26px;
  }

  [data-part-value] b {
    font-size: 50px;
    font-style: italic;
    font-variant-numeric: tabular-nums;
    font-weight: 900;
    line-height: 0.86;
  }

  [data-part-value] small {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 13px;
    font-weight: 600;
    line-height: 1.15;
    white-space: nowrap;
  }

  [data-part="impact"] [data-part-value] {
    color: #e7eef1;
  }

  [data-part="style"] [data-part-value] {
    color: #b9a6ff;
  }

  [data-meter] {
    background: ${({ theme }) => rgba(theme.colors.ice, 0.1)};
    border-radius: 999px;
    height: 6px;
    overflow: hidden;
  }

  [data-meter] i {
    border-radius: 999px;
    display: block;
    height: 100%;
    transition: width 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }

  [data-part="impact"] [data-meter] i {
    background: linear-gradient(90deg, #9fb1ba, #fff);
  }

  [data-part="style"] [data-meter] i {
    background: linear-gradient(90deg, #7b63ff, #e0d6ff);
  }

  @media (prefers-reduced-motion: reduce) {
    [data-meter] i {
      transition: none;
    }
  }

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    grid-template-columns: minmax(0, 1fr);

    [data-score] > b {
      font-size: 104px;
    }
  }

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    gap: 20px;
    padding: 22px 18px;

    [data-parts] {
      grid-template-columns: minmax(0, 1fr);
    }

    [data-plus] {
      display: none;
    }

    [data-part-value] b {
      font-size: 42px;
    }
  }
`;

export const RbLens = styled.section`
  background: linear-gradient(180deg, #203240, #1b2933 50%);
  border-radius: 22px;
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha20};
  display: grid;
  gap: 22px;
  padding: 22px 26px 26px;

  ${rbSwitch}

  [data-lens-head] {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: space-between;
  }

  [data-lens-head] p {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 14px;
    margin: 0;
  }

  [data-chart] {
    min-height: 260px;
  }

  [data-bars],
  [data-axis] {
    display: grid;
    gap: 6px;
    grid-template-columns: repeat(var(--n), minmax(0, 1fr));
  }

  [data-bars] {
    height: 220px;
    margin-top: 34px;
    position: relative;
  }

  [data-col] {
    background: none;
    border: 0;
    border-radius: 5px;
    cursor: default;
    padding: 0;
    position: relative;
  }

  [data-col]:hover,
  [data-col]:focus-visible {
    background: ${({ theme }) => rgba(theme.colors.ice, 0.05)};
    outline: none;
  }

  [data-col] > i {
    background: #ff7a66;
    border-radius: 4px;
    left: 18%;
    opacity: 0.85;
    position: absolute;
    right: 18%;
    top: 50%;
  }

  [data-col] > i[data-up] {
    background: #a6e35c;
    bottom: 50%;
    opacity: 1;
    top: auto;
  }

  [data-bolt] {
    color: #b9a6ff;
    left: 50%;
    position: absolute;
    transform: translateX(-50%);
  }

  [data-bolt] svg {
    height: 14px;
    width: 14px;
  }

  [data-zero],
  [data-half] {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    pointer-events: none;
    position: absolute;
  }

  [data-zero] {
    height: 1px;
    left: 0;
    right: 0;
    top: 50%;
  }

  [data-half] {
    bottom: 0;
    top: -30px;
    width: 1px;
  }

  [data-side-label] {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 12px;
    font-weight: 700;
    left: 0;
    letter-spacing: 0.5px;
    position: absolute;
    text-transform: uppercase;
    top: -34px;
  }

  [data-axis] {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    margin-top: 10px;
    text-align: center;
  }

  [data-list] {
    display: grid;
    gap: 14px;
    list-style: none;
    margin: 0;
    max-width: 760px;
    padding: 0;
  }

  [data-credit],
  [data-moment] {
    align-items: center;
    color: ${({ theme }) => theme.colors.shade200};
    display: grid;
    font-size: 15px;
    font-weight: 600;
    gap: 16px;
  }

  [data-credit] {
    grid-template-columns: 120px minmax(0, 1fr) 56px;
  }

  [data-moment] {
    grid-template-columns: 44px minmax(0, 1fr) 64px;
  }

  [data-track] {
    position: relative;
  }

  [data-credit] [data-track] {
    height: 10px;
  }

  [data-credit] [data-track]::before {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    bottom: -5px;
    content: "";
    left: 50%;
    position: absolute;
    top: -5px;
    width: 1px;
  }

  [data-credit] [data-track] i {
    background: #ff7a66;
    border-radius: 999px;
    bottom: 0;
    position: absolute;
    right: 50%;
    top: 0;
  }

  [data-credit] [data-track] i[data-up] {
    background: #a6e35c;
    left: 50%;
    right: auto;
  }

  [data-credit] b,
  [data-moment] b {
    font-size: 18px;
    font-style: italic;
    font-variant-numeric: tabular-nums;
    font-weight: 900;
    text-align: right;
  }

  [data-credit] b {
    color: #ff7a66;
  }

  [data-credit] b[data-up] {
    color: #a6e35c;
  }

  [data-round] {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 13px;
    font-weight: 700;
  }

  [data-moment] [data-track] {
    background: rgba(185, 166, 255, 0.06);
    border-radius: 8px;
    height: 34px;
    overflow: hidden;
  }

  [data-moment] [data-track] i {
    background: linear-gradient(
      90deg,
      rgba(123, 99, 255, 0.5),
      rgba(185, 166, 255, 0.8)
    );
    border-radius: 8px;
    inset: 0 auto 0 0;
    position: absolute;
  }

  [data-moment] [data-track] span {
    color: ${({ theme }) => theme.colors.ice};
    display: block;
    font-weight: 700;
    line-height: 34px;
    overflow: hidden;
    padding: 0 12px;
    position: relative;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  [data-moment] b {
    color: #b9a6ff;
    font-size: 20px;
  }

  [data-quiet],
  [data-quiet] b {
    color: ${({ theme }) => theme.colors.chalk};
  }

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    padding: 18px 16px 20px;

    [data-lens-head] {
      align-items: stretch;
      flex-direction: column;
    }

    [data-lens-head] [data-switch] button {
      flex: 1;
      justify-content: center;
      padding: 8px 6px;
    }

    [data-bars],
    [data-axis] {
      gap: 2px;
    }

    [data-bars] {
      height: 170px;
    }

    [data-axis] {
      font-size: 9.5px;
    }

    [data-axis] span:nth-child(even) {
      visibility: hidden;
    }

    [data-credit] {
      font-size: 14px;
      gap: 10px;
      grid-template-columns: 96px minmax(0, 1fr) 46px;
    }

    [data-moment] {
      gap: 10px;
      grid-template-columns: 34px minmax(0, 1fr) 50px;
    }
  }
`;

export const RbTip = styled.div`
  background: #0f181e;
  border-radius: 12px;
  box-shadow:
    0 18px 40px -14px rgba(0, 0, 0, 0.85),
    inset 0 0 0 1px ${({ theme }) => rgba(theme.colors.chalk, 0.25)};
  color: ${({ theme }) => theme.colors.shade200};
  display: grid;
  font-size: 13px;
  gap: 4px;
  padding: 11px 13px;
  pointer-events: none;
  position: absolute;
  top: calc(100% + 30px);
  transform: translateX(-50%);
  width: 240px;
  z-index: 3;

  [data-tip-head] {
    align-items: center;
    color: ${({ theme }) => theme.colors.ice};
    display: flex;
    font-weight: 700;
    gap: 8px;
  }

  [data-tip-head] em {
    color: #ff7a66;
    font-style: normal;
    margin-left: auto;
  }

  [data-tip-head] em[data-won] {
    color: #a6e35c;
  }

  b {
    color: #ff7a66;
  }

  b[data-up] {
    color: #a6e35c;
  }

  b[data-style] {
    color: #b9a6ff;
  }

  small {
    color: ${({ theme }) => theme.colors.chalk};
  }
`;

// --- Duels ------------------------------------------------------------------
// A rail of opponents beside one head-to-head panel. You are lime, they are
// poor-stat salmon, StarGuard is amber.

export const DuelLayout = styled.div`
  align-items: start;
  display: grid;
  gap: 20px;
  grid-template-columns: 240px minmax(0, 1fr);

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

// The head-to-head leads; the reel follows as a band under it.
export const DuelMain = styled.div`
  display: grid;
  gap: 20px;
  min-width: 0;
`;

export const DuelRail = styled.div`
  display: grid;
  gap: 6px;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    display: flex;
    overflow-x: auto;
    scrollbar-width: none;
  }
`;

export const DuelOpp = styled.button`
  align-items: center;
  appearance: none;
  background: none;
  border: 0;
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: grid;
  font: inherit;
  gap: 12px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  padding: 10px 14px 10px 10px;
  text-align: left;

  &:hover,
  &[aria-pressed="true"] {
    background: ${({ theme }) => theme.colors.ned};
  }

  &[aria-pressed="true"] {
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha40};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  [data-name] {
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  [data-name] svg {
    color: ${({ theme }) => theme.colors.statLeader};
    margin-left: 6px;
    vertical-align: -2px;
  }

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    flex: none;
    grid-template-columns: auto auto auto;
    padding: 6px 12px 6px 6px;
  }
`;

// Whoever leads takes their colour; an even score stays ice.
export const DuelTally = styled.span<{ $size?: "rail" | "hero" }>`
  align-items: baseline;
  display: inline-flex;
  font-size: ${({ $size }) => ($size === "hero" ? "56px" : "20px")};
  font-style: italic;
  font-variant-numeric: tabular-nums;
  font-weight: 900;
  gap: ${({ $size }) => ($size === "hero" ? "14px" : "3px")};
  line-height: 0.9;
  white-space: nowrap;

  [data-won] {
    color: ${({ theme }) => theme.colors.statElite};
  }

  [data-lost] {
    color: ${({ theme }) => theme.colors.statPoor};
  }

  i {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: ${({ $size }) => ($size === "hero" ? "36px" : "inherit")};
  }

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    font-size: ${({ $size }) => ($size === "hero" ? "40px" : "16px")};

    i {
      font-size: ${({ $size }) => ($size === "hero" ? "26px" : "inherit")};
    }
  }
`;

export const DuelPanel = styled.section`
  background: ${({ theme }) => theme.colors.ned};
  border-radius: 16px;
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha20};
  display: grid;
  gap: 18px;
  min-width: 0;
  padding: 24px;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    padding: 16px;
  }
`;

export const DuelFaces = styled.div`
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr auto 1fr;

  [data-side] {
    align-items: center;
    display: flex;
    gap: 14px;
    min-width: 0;
  }

  [data-side="them"] {
    flex-direction: row-reverse;
    text-align: right;
  }

  [data-side] > span {
    display: grid;
    gap: 4px;
    justify-items: start;
    min-width: 0;
  }

  [data-side="them"] > span {
    justify-items: end;
  }

  [data-side] b {
    font-size: 18px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  [data-face] {
    align-items: center;
    background: ${({ theme }) => theme.colors.navy50};
    border-radius: 50%;
    box-shadow:
      0 0 0 2px ${({ theme }) => theme.colors.ned},
      0 0 0 4px ${({ theme }) => theme.colors.statPoor};
    display: grid;
    flex: none;
    font-size: 22px;
    font-weight: 900;
    height: 64px;
    justify-items: center;
    object-fit: cover;
    width: 64px;
  }

  [data-side="you"] [data-face] {
    box-shadow:
      0 0 0 2px ${({ theme }) => theme.colors.ned},
      0 0 0 4px ${({ theme }) => theme.colors.statElite};
  }

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    gap: 10px;

    [data-side],
    [data-side="them"] {
      flex-direction: column;
      gap: 8px;
      text-align: center;
    }

    [data-side] > span,
    [data-side="them"] > span {
      justify-items: center;
    }

    [data-side] b {
      font-size: 15px;
      max-width: 100px;
    }

    [data-face] {
      font-size: 18px;
      height: 48px;
      width: 48px;
    }
  }
`;

export const DuelStats = styled.div`
  display: grid;

  > header {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  h3 {
    font-size: 16px;
    margin: 0;
  }

  [data-switch] {
    background: ${({ theme }) => theme.colors.navy600};
    border-radius: 9px;
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha20};
    display: inline-flex;
    gap: 2px;
    padding: 3px;
  }

  [data-switch] button {
    background: none;
    border: 0;
    border-radius: 7px;
    color: ${({ theme }) => theme.colors.chalk};
    cursor: pointer;
    font: inherit;
    font-size: 12.5px;
    font-weight: 700;
    padding: 5px 10px;
  }

  [data-switch] button[aria-pressed="true"] {
    background: ${({ theme }) => rgba(theme.colors.ice, 0.1)};
    color: ${({ theme }) => theme.colors.ice};
  }

  [data-row] {
    align-items: center;
    display: grid;
    gap: 12px;
    grid-template-columns: 64px 1fr 150px 1fr 64px;
    padding: 7px 0;
  }

  [data-row] + [data-row] {
    border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  }

  [data-row] > b {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 19px;
    font-style: italic;
    font-variant-numeric: tabular-nums;
    font-weight: 900;
  }

  [data-row] > b:first-child {
    text-align: right;
  }

  [data-row] > b[data-you] {
    color: ${({ theme }) => theme.colors.statElite};
  }

  [data-row] > b[data-them] {
    color: ${({ theme }) => theme.colors.statPoor};
  }

  [data-row] > span {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-align: center;
    text-transform: uppercase;
  }

  [data-row] > span small {
    display: block;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
  }

  [data-bar] {
    background: ${({ theme }) => rgba(theme.colors.ice, 0.06)};
    border-radius: 3px;
    height: 6px;
    overflow: hidden;
    position: relative;
  }

  [data-bar] > i {
    background: ${({ theme }) => rgba(theme.colors.ice, 0.18)};
    border-radius: 3px;
    bottom: 0;
    position: absolute;
    top: 0;
  }

  [data-bar="you"] > i {
    right: 0;
  }

  [data-bar="them"] > i {
    left: 0;
  }

  [data-bar="you"][data-win] > i {
    background: ${({ theme }) => theme.colors.statElite};
  }

  [data-bar="them"][data-win] > i {
    background: ${({ theme }) => theme.colors.statPoor};
  }

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    [data-row] {
      gap: 8px;
      grid-template-columns: 44px 1fr 104px 1fr 44px;
    }

    [data-row] > b {
      font-size: 16px;
    }

    [data-row] > span {
      font-size: 10.5px;
    }
  }
`;

// Two perspective cards, each with that player's agent over the map; the picked
// one is outlined in its player's colour.
export const DuelReel = styled.section`
  background: ${({ theme }) => theme.colors.ned};
  border-radius: 16px;
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha20};
  display: grid;
  gap: 14px;
  padding: 20px 22px 22px;

  h3 {
    font-size: 21px;
    line-height: 1.2;
    margin: 0;
    text-wrap: balance;
  }

  h3 + p {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 14px;
    margin: -8px 0 0;
  }

  [data-pick] {
    display: grid;
    gap: 12px;
    grid-template-columns: 1fr 1fr;
  }

  [data-side] {
    background: ${({ theme }) => theme.colors.navy400} center 40% / cover;
    border: 0;
    border-radius: 14px;
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.chalkAlpha20};
    color: ${({ theme }) => theme.colors.ice};
    cursor: pointer;
    font: inherit;
    isolation: isolate;
    min-height: 170px;
    overflow: hidden;
    padding: 0;
    position: relative;
    text-align: left;
    transition: filter 0.2s ease;
  }

  [data-side]::before {
    background: linear-gradient(
      90deg,
      ${rgba("#0b1116", 0.9)} 0%,
      ${rgba("#0b1116", 0.55)} 55%,
      ${rgba("#0b1116", 0.2)}
    );
    content: "";
    inset: 0;
    position: absolute;
    z-index: -1;
  }

  [data-watch] {
    align-items: center;
    background: ${({ theme }) => theme.colors.envy};
    border-radius: 50%;
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.45);
    color: ${({ theme }) => theme.colors.ice};
    display: flex;
    height: 52px;
    justify-content: center;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 52px;
  }

  [data-side][aria-pressed="false"]:not([data-ready]) {
    filter: saturate(0.4) brightness(0.75);
  }

  [data-side="them"][aria-pressed="true"] {
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.statPoor};
  }

  [data-side="you"][aria-pressed="true"] {
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.statElite};
  }

  // Agent art is 4:3 with the figure centred, so it's pushed right of the text.
  [data-agent] {
    bottom: -30%;
    filter: drop-shadow(0 10px 22px rgba(0, 0, 0, 0.7));
    height: 125%;
    position: absolute;
    right: -16%;
    z-index: -1;
  }

  [data-check] {
    align-items: center;
    background: ${({ theme }) => theme.colors.ice};
    border-radius: 50%;
    color: ${({ theme }) => theme.colors.navy900};
    display: flex;
    height: 24px;
    justify-content: center;
    position: absolute;
    right: 12px;
    top: 12px;
    width: 24px;
  }

  [data-text] {
    bottom: 14px;
    display: grid;
    gap: 4px;
    left: 16px;
    position: absolute;
    right: 40%;
  }

  [data-side] b {
    align-items: center;
    display: flex;
    font-size: 18px;
    gap: 8px;
  }

  [data-side="them"][aria-pressed="true"] b {
    color: ${({ theme }) => theme.colors.statPoor};
  }

  [data-side="you"][aria-pressed="true"] b {
    color: ${({ theme }) => theme.colors.statElite};
  }

  [data-side] b img {
    border-radius: 50%;
    flex: none;
    height: 24px;
    object-fit: cover;
    width: 24px;
  }

  [data-side] small {
    color: ${({ theme }) => theme.colors.shade200};
    font-size: 13px;
  }

  [data-go] {
    align-items: center;
    background: ${({ theme }) => theme.colors.envy};
    border: 0;
    border-radius: 999px;
    color: ${({ theme }) => theme.colors.ice};
    cursor: pointer;
    display: inline-flex;
    font: inherit;
    font-size: 15px;
    font-weight: 700;
    gap: 8px;
    justify-content: center;
    justify-self: end;
    padding: 12px 22px;
  }

  [data-go]:disabled {
    cursor: default;
    opacity: 0.5;
  }

  [data-side] [data-rendering] {
    align-items: center;
    background: ${rgba("#0b1116", 0.72)};
    display: flex;
    gap: 12px;
    inset: 0;
    justify-content: center;
    position: absolute;
  }

  [data-rendering] > span {
    display: grid;
  }

  [data-rendering] svg {
    height: 30px;
    overflow: visible;
    width: 30px;
  }

  [data-rendering] path {
    fill: none;
    stroke-linejoin: round;
  }

  [data-rendering] path:first-child {
    stroke: ${({ theme }) => rgba(theme.colors.chalk, 0.3)};
    stroke-width: 1.4;
  }

  [data-rendering] path:last-child {
    animation: ${starDraw} 1.8s cubic-bezier(0.65, 0, 0.35, 1) infinite;
    stroke: ${({ theme }) => theme.colors.statElite};
    stroke-dasharray: 100;
    stroke-linecap: round;
    stroke-width: 1.8;
  }

  [data-rendering] small {
    color: ${({ theme }) => theme.colors.statElite};
    font-weight: 700;
  }

  button:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    [data-rendering] path:last-child {
      animation: none;
      stroke-dasharray: none;
    }
  }

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    padding: 16px;

    [data-pick] {
      grid-template-columns: minmax(0, 1fr);
    }

    [data-side] {
      min-height: 130px;
    }

    [data-go] {
      justify-self: stretch;
    }
  }
`;

export const DuelNotes = styled.div`
  display: grid;
  gap: 10px;

  > p {
    align-items: center;
    color: ${({ theme }) => theme.colors.chalk};
    display: flex;
    flex-wrap: wrap;
    font-size: 14px;
    gap: 8px;
    margin: 0;
  }

  > p > svg {
    flex: none;
  }

  > p > span {
    flex: 1;
    min-width: 0;
  }

  > p[data-starguard] {
    color: ${({ theme }) => theme.colors.statLeader};
    font-weight: 600;
  }

  button {
    appearance: none;
    background: none;
    border: 0;
    color: ${({ theme }) => theme.colors.chalk};
    cursor: pointer;
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    padding: 0;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  button:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;
